import Map, {
  Marker,
  Popup,
  Layer,
  Source,
  NavigationControl,
} from "react-map-gl/mapbox";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "@remix-run/react";
import MapCard from "../MapCards/MapCards";
import supercluster from "supercluster";
import debounce from "lodash.debounce";
import { Check, Expand, Info, PanelLeftOpen, PanelRightOpen, RotateCcw, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import convertCurrency from "~/lib/helpers/currencyConverter";
import { useCurrentCurrency } from "~/lib/helpers/CurrencyHelper";
import {
  buildPropertyDetailsFilterQuery,
  getGlobalPriceTierPresent,
  getSelectedTiersFromPriceRange,
  getSelectedTiersPriceFilter,
  resolveGlobalPriceTierCurrency,
  type GlobalPriceTier,
} from "~/lib/helpers/mapPinsHelper";
import { getRegionConfigByCountryCodeRegion } from "custom-config/region.configs";
import { getPersistedSearchLocationContext } from "~/lib/helpers/searchPlaceContext";
import PropertiesService from "~/lib/services/PropertiesService";

const getMapboxAccessToken = (): string => {
  return process.env.PUBLIC_REMIX_APP_MAPBOX_ACCESS_TOKEN ?? "";
};

const getMapboxStyle = (): string => {
  return process.env.PUBLIC_REMIX_APP_MAPBOX_STYLE ?? "";
};

type RegionMapConfig = {
  lat?: number;
  lng?: number;
  latitude?: number;
  longitude?: number;
};

/** Region configs store either `lat`/`lng` or `latitude`/`longitude`. */
function readRegionMapPoint(region?: string): { latitude: number; longitude: number } | null {
  const config = getRegionConfigByCountryCodeRegion(region || "") as RegionMapConfig | null;
  if (!config) return null;
  const latitude = Number(config.latitude ?? config.lat);
  const longitude = Number(config.longitude ?? config.lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

function normalizeCountryCode(code?: string): string {
  const value = (code || "").toLowerCase().trim();
  return value === "gb" ? "uk" : value;
}

/** AutoPlaces stores lat/lng off the URL. Use them when they belong to this country. */
function readSearchMapPoint(region?: string): { latitude: number; longitude: number } | null {
  const ctx = getPersistedSearchLocationContext();
  if (!ctx) return null;
  if (normalizeCountryCode(ctx.country) !== normalizeCountryCode(region)) return null;
  const latitude = Number(ctx.latitude);
  const longitude = Number(ctx.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

type LocationPolygonBounds = {
  minLatitude: number;
  maxLatitude: number;
  minLongitude: number;
  maxLongitude: number;
};

function visitGeoJsonCoordinates(coords: unknown, visit: (lng: number, lat: number) => void) {
  if (!Array.isArray(coords)) return;
  if (coords.length >= 2 && typeof coords[0] === "number" && typeof coords[1] === "number") {
    visit(coords[0], coords[1]);
    return;
  }
  coords.forEach((part) => visitGeoJsonCoordinates(part, visit));
}

function getBoundsFromFeatureCollection(featureCollection: any): LocationPolygonBounds | null {
  const features = featureCollection?.features;
  if (!Array.isArray(features) || !features.length) return null;

  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  features.forEach((feature: any) => {
    visitGeoJsonCoordinates(feature?.geometry?.coordinates, (lng, lat) => {
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });
  });

  if (!Number.isFinite(minLng) || !Number.isFinite(minLat)) return null;
  return {
    minLatitude: minLat,
    maxLatitude: maxLat,
    minLongitude: minLng,
    maxLongitude: maxLng,
  };
}

function resolveLocationPolygonBounds(
  featureCollection: any,
  apiBounds?: LocationPolygonBounds | null,
): LocationPolygonBounds | null {
  // Only treat a real drawable polygon as fit-to-polygon. The API sometimes
  // returns boundingBox with no geometry — fall through to marker fitting then.
  const hasPolygonFeatures =
    Array.isArray(featureCollection?.features) && featureCollection.features.length > 0;
  if (!hasPolygonFeatures) return null;
  return getBoundsFromFeatureCollection(featureCollection) ?? apiBounds ?? null;
}

function parseQueryViewportBounds(
  query?: Record<string, string | number> | null,
): LocationPolygonBounds | null {
  if (!query) return null;
  const minLatitude = Number(query.minLatitude);
  const maxLatitude = Number(query.maxLatitude);
  const minLongitude = Number(query.minLongitude);
  const maxLongitude = Number(query.maxLongitude);
  if (![minLatitude, maxLatitude, minLongitude, maxLongitude].every(Number.isFinite)) {
    return null;
  }
  if (minLatitude >= maxLatitude || minLongitude >= maxLongitude) return null;
  return { minLatitude, maxLatitude, minLongitude, maxLongitude };
}

function getZoomForBounds(bounds: LocationPolygonBounds): number {
  const latSpan = Math.max(bounds.maxLatitude - bounds.minLatitude, 1e-6);
  const lngSpan = Math.max(bounds.maxLongitude - bounds.minLongitude, 1e-6);
  const span = Math.max(latSpan, lngSpan);
  return Math.min(Math.max(Math.log2(360 / span) - 1, 1), 18);
}

const NEARBY_PIN_ICON = "/assets/icons/map-pin-black.png";
const NEARBY_PIN_ICON_SIZE = 0.21;
const NEARBY_PIN_PIXEL_RATIO = 2;
const MAP_PIN_IMAGE_WIDTH = 379;
/** Match HTML markers to Mapbox nearby-pin icon-size (379px @2x * 0.21 ≈ 40px). */
const PROPERTY_PIN_SIZE = Math.round(
  (MAP_PIN_IMAGE_WIDTH / NEARBY_PIN_PIXEL_RATIO) * NEARBY_PIN_ICON_SIZE,
);
/**
 * Map pins and search boundary — shades of #303e49.
 */
const MAP_POLYGON_FILL = "#303e49";
const MAP_POLYGON_OUTLINE = "#303e49";
const MAP_CLUSTER_DARK = "#1e4e5c";
const MAP_CLUSTER_MID = "#2b6e84";
const MAP_CLUSTER_LIGHT = "#2b758e";
const MAP_PIN_HOVER_DARK = "#c04d2b";
const MAP_PIN_HOVER_MID = "#e86749";
const MAP_PIN_HOVER_LIGHT = "#f5926f";
const CLUSTER_PIN_BASE_SIZE = PROPERTY_PIN_SIZE + 14;
const MAP_POPUP_WIDTH = 269;
/** MapCard renders PGridCard at 70% zoom — use scaled size for edge detection. */
const MAP_POPUP_HEIGHT = 280;
const MAP_POPUP_EDGE_PADDING = 12;
const MAP_POPUP_PIN_GAP = 4;

export type MapPinPriceTier = "Silver" | "Gold" | "Platinum";

const PRICE_TIER_RANK: Record<MapPinPriceTier, number> = {
  Silver: 1,
  Gold: 2,
  Platinum: 3,
};

type PinGradientColors = {
  dark: string;
  mid: string;
  light: string;
  inner: string;
};

/** Tier pin colours — semantic (Silver=budget, Gold=mid-range, Platinum=premium). */
const MAP_PIN_TIER_COLORS: Record<MapPinPriceTier, PinGradientColors> = {
  Silver: {
    dark: "#3a3e44",
    mid: "#4C5056",
    light: "#5c6168",
    inner: "#F4F5F7",
  },
  Platinum: {
    dark: "#7d6636",
    mid: "#947A42",
    light: "#a88b4f",
    inner: "#FEF4E4",
  },
  Gold: {
    dark: "#0e5576",
    mid: "#116991",
    light: "#1a82b0",
    inner: "#E9F7FD",
  },
};


const MAP_PIN_TIER_LEGEND: Record<
  MapPinPriceTier,
  { label: string; description: string }
> = {
  Silver: { label: "Starter", description: "for entry level" },
  Gold: { label: "Best Value", description: "for professionals" },
  Platinum: { label: "Premium", description: "Enterprise Offices" },
};

function resolvePinMonthlyPrice(pin: any, showPerDesk: boolean): number | null {
  const raw = showPerDesk
    ? pin?.avgMonthlyCostPerWorkstation || pin?.costPerWorkstation
    : pin?.priceMonthly;
  const price = Number(raw);
  return Number.isFinite(price) && price > 0 ? price : null;
}

function toComparablePrice(
  price: number,
  fromCurrency: string | undefined,
  targetCurrency: string,
): number | null {
  const plain = convertCurrency(price, fromCurrency ?? targetCurrency, targetCurrency, true);
  const comparable = Number(String(plain).replace(/,/g, ""));
  return Number.isFinite(comparable) ? comparable : null;
}

const MAP_PIN_TIER_ORDER: MapPinPriceTier[] = ["Silver", "Gold", "Platinum"];

/** Survives ClientOnly remounts so legend toggles are not overwritten by stale URL hydrations. */
let mapPinTierSelectionCache: {
  locationKey: string;
  tiers: MapPinPriceTier[];
} | null = null;

function normalizePriceTier(value: unknown): MapPinPriceTier | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === "silver") return "Silver";
  if (normalized === "gold") return "Gold";
  if (normalized === "platinum") return "Platinum";
  return null;
}

function resolvePriceTier(source: any): MapPinPriceTier | null {
  return normalizePriceTier(source?.priceTier ?? source?.price_tier);
}

function resolveFeaturePinRecord(item: any) {
  return item?.data ?? item?.properties ?? item;
}

function resolveHighestPriceTier(
  tiers: Array<MapPinPriceTier | null | undefined>,
): MapPinPriceTier | null {
  let best: MapPinPriceTier | null = null;
  let bestRank = 0;
  tiers.forEach((tier) => {
    if (!tier) return;
    const rank = PRICE_TIER_RANK[tier];
    if (rank > bestRank) {
      best = tier;
      bestRank = rank;
    }
  });
  return best;
}

/** Prefer the cheapest/lowest tier so mixed clusters show Starter over Best Value/Premium. */
function resolveLowestPriceTier(
  tiers: Array<MapPinPriceTier | null | undefined>,
): MapPinPriceTier | null {
  let best: MapPinPriceTier | null = null;
  let bestRank = Number.POSITIVE_INFINITY;
  tiers.forEach((tier) => {
    if (!tier) return;
    const rank = PRICE_TIER_RANK[tier];
    if (rank < bestRank) {
      best = tier;
      bestRank = rank;
    }
  });
  return best;
}

function getPinColors(
  priceTier: MapPinPriceTier | null | undefined,
  isHovered: boolean,
  _pinCount = 0,
  _marker = false,
): PinGradientColors {
  if (isHovered) {
    return {
      dark: MAP_PIN_HOVER_DARK,
      mid: MAP_PIN_HOVER_MID,
      light: MAP_PIN_HOVER_LIGHT,
      inner: "#FEF0EB",
    };
  }

  return priceTier
    ? MAP_PIN_TIER_COLORS[priceTier]
    : {
      dark: MAP_CLUSTER_DARK,
      mid: MAP_CLUSTER_MID,
      light: MAP_CLUSTER_LIGHT,
      inner: "#E9F7FD",
    };
}

function getPinLabelColor(
  priceTier: MapPinPriceTier | null | undefined,
  isHovered: boolean,
): string {
  if (isHovered) return MAP_PIN_HOVER_DARK;
  return priceTier ? MAP_PIN_TIER_COLORS[priceTier].dark : MAP_CLUSTER_DARK;
}

function getTierBorderColor(priceTier: MapPinPriceTier | null | undefined): string {
  const colors = priceTier ? MAP_PIN_TIER_COLORS[priceTier] : null;
  return colors?.mid ?? MAP_CLUSTER_MID;
}

function getTierFillColor(priceTier: MapPinPriceTier | null | undefined): string {
  const colors = priceTier ? MAP_PIN_TIER_COLORS[priceTier] : null;
  return colors?.mid ?? MAP_CLUSTER_MID;
}

function resolvePinCoordinates(source: any): { lng: number; lat: number } | null {
  if (!source) return null;
  const lng = Number(
    source.geolocationLong ?? source.lng ?? source.longitude ?? source.geometry?.coordinates?.[0],
  );
  const lat = Number(
    source.geolocationLat ?? source.lat ?? source.latitude ?? source.geometry?.coordinates?.[1],
  );
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return { lng, lat };
}

const VIEWPORT_QUERY_KEYS = [
  "minLatitude",
  "maxLatitude",
  "minLongitude",
  "maxLongitude",
  "latitude",
  "longitude",
  "zoom",
  "page",
] as const;

function buildFilterQueryKey(query?: string): string {
  if (!query) return "";
  const params = new URLSearchParams(query.startsWith("?") ? query.slice(1) : query);
  VIEWPORT_QUERY_KEYS.forEach((key) => params.delete(key));
  return params.toString();
}

function collectMapPinRecord(pin: any) {
  return pin?.data ?? pin;
}

function collectMapPinIds(pins: any[]): Set<string> {
  const ids = new Set<string>();
  pins.forEach((pin) => {
    const record = collectMapPinRecord(pin);
    [record?.id, record?.propertyId, record?.sfid].forEach((value) => {
      if (value != null && value !== "") ids.add(String(value));
    });
  });
  return ids;
}

function matchesMapPinHover(
  data: { id?: unknown; propertyId?: unknown; sfid?: unknown } | null | undefined,
  hoverIds: string[],
): boolean {
  if (!hoverIds.length || !data) return false;
  const pinIds = [data.id, data.propertyId, data.sfid]
    .filter((value) => value != null && value !== "")
    .map(String);
  return hoverIds.some((hoverId) => pinIds.includes(String(hoverId)));
}

type MapPinTierLegendProps = {
  tiers: MapPinPriceTier[];
  selectedTiers: MapPinPriceTier[];
  onToggleTier: (tier: MapPinPriceTier) => void;
  tierStartingPrices?: Partial<Record<MapPinPriceTier, { price: number; currencyIsoCode?: string }>>;
  currency: string;
  compact?: boolean;
};

const MapPinTierLegend: React.FC<MapPinTierLegendProps> = ({
  tiers,
  selectedTiers,
  onToggleTier,
  tierStartingPrices = {},
  currency,
  compact = false,
}) => {
  const [expanded, setExpanded] = useState(true);
  const visibleTiers = MAP_PIN_TIER_ORDER.filter((tier) => tiers.includes(tier));
  const selectedSet = useMemo(() => new Set(selectedTiers), [selectedTiers]);
  const noneSelected = visibleTiers.every((tier) => !selectedSet.has(tier));

  if (!visibleTiers.length) return null;

  const iconSize = compact ? 11 : 12;

  if (!expanded) {
    return (
      <div
        className={`absolute z-[10000] ${compact ? "bottom-[12%] left-2" : "bottom-4 left-3"}`}
        role="group"
        aria-label="Map pin price tier legend"
      >
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className={`flex items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-600 shadow-md backdrop-blur-sm hover:bg-gray-50 ${compact ? "h-7 w-7" : "h-8 w-8"}`}
          aria-expanded={false}
          aria-controls="map-pin-tier-legend-items"
          aria-label="Show pin colour legend"
        >
          <Info size={iconSize} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`absolute z-[10000] ${compact ? "bottom-[12%] left-2" : "bottom-4 left-3"}`}
      role="group"
      aria-label="Map pin price tier legend"
    >
      <div className="relative">
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className={`absolute -right-2 -top-2 z-10 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-oh-secondary ${compact ? "h-5 w-5" : "h-6 w-6"}`}
          aria-expanded={true}
          aria-controls="map-pin-tier-legend-items"
          aria-label="Close pin colour legend"
        >
          <X size={iconSize} aria-hidden="true" />
        </button>

        <div
          className={`rounded-lg border border-gray-200/80 bg-white/80 shadow-md backdrop-blur-md ${compact ? "w-[178px]" : "w-[240px]"}`}
        >
          <ul
            id="map-pin-tier-legend-items"
            className={compact ? "space-y-0.5 px-1 py-1 pr-2" : "space-y-0.5 px-1.5 py-1.5 pr-2.5"}
          >
            {visibleTiers.map((tier) => {
              const { label, description } = MAP_PIN_TIER_LEGEND[tier];
              const isSelected = selectedSet.has(tier);
              const startingPrice = tierStartingPrices[tier];
              const formattedStartingPrice = startingPrice
                ? convertCurrency(
                  startingPrice.price,
                  startingPrice.currencyIsoCode ?? currency,
                  currency,
                )
                : null;

              return (
                <li key={tier}>
                  <button
                    type="button"
                    onClick={() => onToggleTier(tier)}
                    className={`flex w-full items-start rounded-md text-left transition-colors hover:bg-gray-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-oh-secondary ${compact ? "gap-1 px-1 py-0.5" : "gap-1.5 px-1.5 py-1"} ${isSelected ? "" : "opacity-70"}`}
                    aria-pressed={isSelected}
                  >
                    <Checkbox
                      checked={isSelected}
                      className={`pointer-events-none shrink-0 rounded-[3px] border-oh-primary data-[state=checked]:bg-oh-primary data-[state=checked]:border-oh-primary data-[state=checked]:text-white ${compact
                        ? "mt-px h-3 w-3 [&_svg]:h-2 [&_svg]:w-2 [&_svg]:stroke-[3]"
                        : "mt-px h-3.5 w-3.5 [&_svg]:h-2.5 [&_svg]:w-2.5 [&_svg]:stroke-[3]"
                        }`}
                      aria-hidden="true"
                      tabIndex={-1}
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className={`block leading-tight ${compact ? "text-[11px]" : "text-xs"}`}
                      >
                        <span
                          className={`inline-block rounded px-1 py-px font-semibold text-white ${compact ? "text-[10px]" : "text-[11px]"}`}
                          style={{ backgroundColor: MAP_PIN_TIER_COLORS[tier].mid }}
                        >
                          {label}
                        </span>
                        <span className={isSelected ? "text-gray-500" : "text-gray-400"}>
                          {" "}
                          — {description}
                        </span>
                      </span>
                      {formattedStartingPrice ? (
                        <span
                          className={`mt-0.5 block font-medium ${compact ? "text-[10px]" : "text-[11px]"} ${isSelected ? "text-gray-600" : "text-gray-400"}`}
                        >
                          Starting from {formattedStartingPrice}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {noneSelected ? (
            <div
              className={`border-t border-gray-100 text-gray-500 ${compact ? "px-1.5 py-1 text-[10px]" : "px-2 py-1.5 text-[11px]"}`}
            >
              All tiers hidden — no pins will show.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export type MapMarkerVariant = "price-pill" | "teal-pin";

type ClusterPinSvgProps = {
  size: number;
  className?: string;
  isHovered?: boolean;
  pinCount?: number;
  marker?: boolean;
  priceTier?: MapPinPriceTier | null;
  label?: string | null;
  labelFontSize?: number;
};

/** Teardrop cluster pins — solid tier body with a light inner disc for the count. */
const ClusterPinSvg: React.FC<ClusterPinSvgProps> = ({
  size,
  className = "",
  isHovered = false,
  pinCount = 0,
  marker = false,
  priceTier = null,
  label = null,
  labelFontSize = 10,
}) => {
  const colors = getPinColors(priceTier, isHovered, pinCount, marker);
  const labelColor = getPinLabelColor(priceTier, isHovered);
  // Map CSS px font size into the 24x24 viewBox so text scales with the pin.
  const viewBoxFontSize = (labelFontSize / size) * 24;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={`block ${className}`}
      style={{
        filter: isHovered
          ? "drop-shadow(0 4px 10px rgba(232,103,73,0.55))"
          : "drop-shadow(0 3px 6px rgba(0,0,0,0.28))",
      }}
    >
      <path
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
        fill={colors.mid}
      />
      <circle
        cx="12"
        cy="8.5"
        r="4.25"
        fill={colors.inner}
      />
      {label ? (
        <text
          x="12"
          y="8.5"
          textAnchor="middle"
          dominantBaseline="central"
          fill={labelColor}
          fontSize={viewBoxFontSize}
          fontWeight={700}
          style={{ fontFamily: "inherit" }}
        >
          {label}
        </text>
      ) : null}
    </svg>
  );
};

function getClusterPinSize(count: number): number {
  if (count >= 1000) return CLUSTER_PIN_BASE_SIZE + 28;
  if (count >= 500) return CLUSTER_PIN_BASE_SIZE + 22;
  if (count >= 100) return CLUSTER_PIN_BASE_SIZE + 16;
  if (count >= 50) return CLUSTER_PIN_BASE_SIZE + 10;
  if (count >= 10) return CLUSTER_PIN_BASE_SIZE + 6;
  return CLUSTER_PIN_BASE_SIZE;
}

function getClusterLabelFontSize(count: number, label: string): number {
  if (count >= 1000) return 14;
  if (count >= 100) return 13;
  if (label.length > 3) return 10;
  if (label.length > 2) return 11;
  return 10;
}

function formatClusterCount(count: number): string {
  if (count >= 10000) return `${Math.round(count / 1000)}k`;
  if (count >= 1000) return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(count);
}

type ClusterBubbleProps = {
  lat: number;
  long: number;
  count: number;
  onClick: () => void;
  priceTier?: MapPinPriceTier | null;
  isHovered?: boolean;
};

const ClusterBubble: React.FC<ClusterBubbleProps> = ({ lat, long, count, onClick, priceTier = null, isHovered = false }) => {
  const label = formatClusterCount(count);
  const size = getClusterPinSize(count);
  const fontSize = getClusterLabelFontSize(count, label);

  return (
    <Marker longitude={long} latitude={lat} anchor="bottom" style={isHovered ? { zIndex: 3000 } : { zIndex: 2500 }}>
      <div
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className="cursor-pointer select-none transition-transform duration-150 ease-out"
        style={{
          width: size,
          height: size,
          transform: isHovered ? "scale(1.2)" : "scale(1)",
          transformOrigin: "bottom center",
          zIndex: isHovered ? 9999 : "auto",
        }}
      >
        <ClusterPinSvg
          size={size}
          pinCount={count}
          priceTier={priceTier}
          isHovered={isHovered}
          label={label}
          labelFontSize={fontSize}
        />
      </div>
    </Marker>
  );
};

type NearbyPropertyPinProps = {
  lat: number;
  long: number;
  onClick: () => void;
};

const NearbyPropertyPin: React.FC<NearbyPropertyPinProps> = ({ lat, long, onClick }) => (
  <Marker longitude={long} latitude={lat} anchor="bottom" style={{ zIndex: 1500 }}>
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="cursor-pointer"
      style={{
        width: PROPERTY_PIN_SIZE,
        height: PROPERTY_PIN_SIZE,
      }}
    >
      <img
        src={NEARBY_PIN_ICON}
        alt=""
        draggable={false}
        className="block drop-shadow-md"
        style={{
          width: PROPERTY_PIN_SIZE,
          height: PROPERTY_PIN_SIZE,
          minWidth: PROPERTY_PIN_SIZE,
          minHeight: PROPERTY_PIN_SIZE,
          objectFit: "contain",
          display: "block",
        }}
      />
    </div>
  </Marker>
);

/** Pin-count threshold for slightly stronger clustering on dense maps. */
const MAP_DENSE_PIN_THRESHOLD = 50;
const MAP_MAX_ZOOM = 18;
const MAP_MIN_CLUSTER_PIN_COUNT = 2;

/**
 * Supercluster max zoom — above this level the index returns individual leaves.
 * Kept high so small pin sets still cluster when the map is zoomed out.
 */
function getClusterMaxZoom(pinCount: number): number {
  if (pinCount > 2000) return 15;
  if (pinCount > 1000) return 16;
  return MAP_MAX_ZOOM - 1;
}

/**
 * Cluster radius in screen pixels (supercluster). Same pixel radius covers a larger
 * geographic area when zoomed out, so nearby pins merge at low zoom and split apart when zoomed in.
 */
function getClusterRadiusForZoom(zoom: number, pinCount: number): number {
  const zoomLevel = Math.floor(zoom);
  const isDense = pinCount >= MAP_DENSE_PIN_THRESHOLD;

  if (zoomLevel <= 5) return 95 + (isDense ? 12 : 0);
  if (zoomLevel <= 7) return 75 + (isDense ? 12 : 0);
  if (zoomLevel <= 9) return 58 + (isDense ? 10 : 0);
  if (zoomLevel <= 11) return 40 + (isDense ? 8 : 0);
  if (zoomLevel <= 13) return 22 + (isDense ? 4 : 0);
  if (zoomLevel <= 15) return 14 + (isDense ? 2 : 0);
  return 10;
}

function isValidMapPin(pin: any): boolean {
  const lat = pin?.geolocationLat ?? pin?.lat ?? pin?.latitude;
  const lng = pin?.geolocationLong ?? pin?.lng ?? pin?.longitude;
  return Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));
}

type MarkerData = {
  data: any;
  geometry: any;
  geolocationLat: number;
  geolocationLong: number;
  advertisedPriceWeekly: number;
  currencyIsoCode?: string;
  priceTier?: string | null;
};

// Default param `= []` allocates a new array every render when the prop is
// omitted. That invalidates memos/effects that depend on the array identity
// (add-listing does not pass nearbyListings / nearByProperties / hoverIds).
const EMPTY_MARKER_DATA: MarkerData[] = [];
const EMPTY_HOVER_IDS: string[] = [];

function samePriceTiers(
  a: MapPinPriceTier[] | null | undefined,
  b: MapPinPriceTier[] | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((tier, index) => tier === b[index]);
}

type Feature = {
  count?: any;
  type: "Feature";
  properties: { title?: string };
  geometry: { type: "Point"; coordinates: [number, number] };
  data: any;
};

type GeoJSONData = {
  type: "FeatureCollection";
  features: Feature[];
};

type CustomMarkerProps = {
  lat: number;
  long: number;
  text: string;
  setCurrentMarker: () => void;
  count: number;
  isHovered?: boolean;
  variant?: MapMarkerVariant;
  priceTier?: MapPinPriceTier | null;
};

const CustomMarker: React.FC<CustomMarkerProps> = ({
  lat,
  long,
  text,
  setCurrentMarker,
  count,
  isHovered = false,
  variant = "price-pill",
  priceTier = null,
}) => {

  if (variant === "teal-pin") {
    const size = PROPERTY_PIN_SIZE;
    const label = count > 1 ? formatClusterCount(count) : null;
    const fontSize = label ? getClusterLabelFontSize(count, label) : 12;
    const fillColor = isHovered ? MAP_PIN_HOVER_MID : getTierFillColor(priceTier);

    return (
      <Marker
        longitude={long}
        latitude={lat}
        anchor="bottom"
        style={isHovered ? { zIndex: 3000 } : { zIndex: 2000 }}
      >
        {count > 1 ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setCurrentMarker();
            }}
            className="cursor-pointer select-none transition-transform duration-150 ease-out"
            style={{
              zIndex: isHovered ? 9999 : "auto",
              width: size,
              height: size,
              transform: isHovered ? "scale(1.2)" : "scale(1)",
              transformOrigin: "bottom center",
            }}
          >
            <ClusterPinSvg
              size={size}
              isHovered={isHovered}
              pinCount={count}
              marker
              priceTier={priceTier}
              label={label}
              labelFontSize={fontSize}
            />
          </div>
        ) : (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setCurrentMarker();
            }}
            className="cursor-pointer select-none whitespace-nowrap rounded-full px-2.5 py-[3px] text-[12px] font-semibold leading-tight"
            style={{
              backgroundColor: fillColor,
              border: "1.5px solid #fff",
              color: "#fff",
              zIndex: isHovered ? 9999 : "auto",
              transform: isHovered ? "scale(1.08)" : "scale(1)",
              transformOrigin: "bottom center",
              boxShadow: isHovered
                ? "0 4px 12px rgba(0,0,0,0.28)"
                : "0 2px 8px rgba(0,0,0,0.22)",
            }}
            aria-label={`Average cost per workstation: ${text}`}
          >
            {text}
          </div>
        )}
      </Marker>
    );
  }

  return (
    <Marker longitude={long} latitude={lat} style={isHovered ? { zIndex: 3000 } : { zIndex: 2000 }}>
      <div
        onClick={(e) => {
          e.stopPropagation();
          setCurrentMarker();
        }}
        className={`bg-white text-gray-800 text-sm font-semibold px-3 py-1 rounded-2xl shadow-2xl border border-gray-300  
          transition-all duration-200 ease-in-out hover:bg-black hover:text-white cursor-pointer relative`}
        style={{
          zIndex: isHovered ? 9999 : "auto",
          position: "relative",
          backgroundColor: isHovered ? "#000" : undefined,
          color: isHovered ? "#fff" : undefined,
        }}
      >
        {text}
        {count > 1 && (
          <div className="absolute top-[-10px] right-[-10px] flex h-[20px] w-[20px] items-center justify-center rounded-full bg-oh-secondary">
            <p className="text-[12px] text-white">{count}</p>
          </div>
        )}
      </div>
    </Marker>
  );
};

export default function CustomMap({
  data = EMPTY_MARKER_DATA,
  totalListings = 0,
  rightWidth = 0,
  rightContainerRef = { current: null },
  width = 100,
  setWidth = undefined,
  loading = false,
  currentPage = 1,
  handlePageChange = undefined,
  callGeoSearch = undefined,
  displayMode = "",
  nearbyListings = EMPTY_MARKER_DATA,
  nearByProperties = EMPTY_MARKER_DATA,
  mobile,
  hideSearchButton,
  query,
  isScrollZoom = true,
  showNext = true,
  showPerDesk = false,
  isMobile = false,
  handleLocationSelect,
  hoverId = "",
  hoverIds = EMPTY_HOVER_IDS,
  onHover,
  showPanelControl = true,
  isPropertyCard,
  featuredPropertyIds,
  region,
  recalibrate,
  setRecalibrate,
  parsedQuery,
  locationPolygon = null,
  locationPolygonBounds = null,
  globalPriceTier = null,
  isWorkspaceMap = false,
  showSearchAsIMoveOption = true,
  isAreaPage = false,
  markerVariant = "price-pill",
  pageSize = 40,
}: {
  data?: MarkerData[];
  totalListings?: number;
  rightWidth?: number;
  rightContainerRef?: any;
  width?: number;
  setWidth?: Function;
  loading?: boolean;
  currentPage?: number;
  handlePageChange?: Function;
  callGeoSearch?: Function;
  displayMode?: string;
  nearbyListings?: MarkerData[];
  nearByProperties?: MarkerData[];
  mobile: boolean;
  hideSearchButton?: boolean;
  query?: string;
  isScrollZoom?: boolean;
  showNext?: boolean;
  showPerDesk?: boolean;
  isMobile?: boolean;
  handleLocationSelect?: Function;
  hoverId?: string;
  hoverIds?: string[];
  onHover?: Function;
  showPanelControl: boolean;
  isPropertyCard?: boolean;
  featuredPropertyIds?: ReadonlySet<string>;
  region?: string
  recalibrate?: boolean
  setRecalibrate?: Function
  parsedQuery?: Record<string, string>
  locationPolygon?: any
  locationPolygonBounds?: LocationPolygonBounds | null
  globalPriceTier?: GlobalPriceTier | null
  isWorkspaceMap?: boolean
  showSearchAsIMoveOption?: boolean
  isAreaPage?: boolean
  markerVariant?: MapMarkerVariant
  pageSize?: number
}) {
  const mapRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const drawerMapContainerRef = useRef<HTMLDivElement>(null);
  const [currentMarker, setCurrentMarker] = useState<Feature | null>(null);
  const [currentNearbyProperty, setCurrentNearbyProperty] = useState<any | null>(null);
  const [activePopupCoords, setActivePopupCoords] = useState<{ lng: number; lat: number } | null>(null);
  const [mapViewRevision, setMapViewRevision] = useState(0);
  const [nearbyLoading, setNearbyLoading] = useState<{ lat: number, lng: number } | null>(null);
  const [unclusteredPoints, setUnclusteredPoints] = useState<Feature[]>([]);
  const [visibleClusters, setVisibleClusters] = useState<any[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [mapCenter, setMapCenter] = useState<{
    lat: number;
    long: number;
    zoom: number;
  } | null>({
    lat: parsedQuery?.latitude ? parseFloat(parsedQuery.latitude) : -33.86468504237645,
    long: parsedQuery?.longitude ? parseFloat(parsedQuery.longitude) : 151.206046,
    zoom: 5,
  });

  const activeHoverIds = useMemo(() => {
    const ids = new Set<string>();
    hoverIds.forEach((id) => {
      if (id != null && id !== "") ids.add(String(id));
    });
    if (hoverId) ids.add(String(hoverId));
    return [...ids];
  }, [hoverId, hoverIds]);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchAsIMove, setSearchAsIMove] = useState(true);
  const [shouldPulseSearchHere, setShouldPulseSearchHere] = useState(false);
  const hasUserInteractedRef = useRef(false);
  const isProgrammaticMoveRef = useRef(false);
  const hasInitialCalibrationRef = useRef(false);
  const pendingRecalibrateRef = useRef(false);
  const sawLoadingWhilePendingRecalibrateRef = useRef(false);
  const pendingRecalibrateBaselineBoundsKeyRef = useRef<string | null>(null);
  const lastFittedPolygonBoundsKeyRef = useRef<string | null>(null);
  const loadingRef = useRef(!!loading);
  loadingRef.current = !!loading;
  const updateUnclusteredPointsRef = useRef<(() => void) | null>(null);
  const popupRequestIdRef = useRef(0);
  const prevFilterQueryKeyRef = useRef<string | null>(null);

  const clearOpenMarkerPopup = useCallback(() => {
    popupRequestIdRef.current += 1;
    setCurrentMarker(null);
    setCurrentNearbyProperty(null);
    setActivePopupCoords(null);
    setNearbyLoading(null);
    onHover?.(null);
  }, [onHover]);
  const lastSearchContextRef = useRef<{
    placeId: string;
    text: string;
    lat: string;
    lng: string;
    city: string;
    state: string;
    country: string;
  } | null>(null);
  // Track last data to detect when data changes (for back navigation)
  const lastDataRef = useRef<any[]>([]);

  // Keep latest nearby properties for map marker rendering.
  const filteredNearByProperties = useMemo(() => {
    const list = Array.isArray(nearByProperties) ? nearByProperties : [];
    if (!list.length) return [];

    const idSet = new Set<string>();
    const coordSet6 = new Set<string>();
    const coordSet5 = new Set<string>();

    const addId = (value: unknown) => {
      if (value === null || value === undefined) return;
      const s = String(value).trim().toLowerCase();
      if (!s) return;
      idSet.add(s);
    };

    const addCoord = (lat: unknown, lng: unknown) => {
      if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;
      const la = Number(lat);
      const ln = Number(lng);
      coordSet6.add(`${la.toFixed(6)},${ln.toFixed(6)}`);
      coordSet5.add(`${la.toFixed(5)},${ln.toFixed(5)}`);
    };

    // Index all geosearch + nearby-listing markers (these are the ones we want to exclude from "nearby dots")
    [...(Array.isArray(data) ? data : []), ...(Array.isArray(nearbyListings) ? nearbyListings : [])].forEach((p: any) => {
      const record = p?.data ?? p;
      addId(record?.propertyId ?? record?.propertyID ?? record?.sfid ?? record?.id);
      addCoord(p?.geolocationLat ?? record?.geolocationLat ?? record?.lat, p?.geolocationLong ?? record?.geolocationLong ?? record?.lng);
    });

    if (!idSet.size && !coordSet6.size && !coordSet5.size) return list;

    return list.filter((p: any) => {
      const candidates = [p?.propertyId, p?.propertyID, p?.sfid, p?.id];
      const hasMatchingId = candidates.some((v) => {
        if (v === null || v === undefined) return false;
        const s = String(v).trim().toLowerCase();
        return s ? idSet.has(s) : false;
      });
      if (hasMatchingId) return false;

      const lat = p?.geolocationLat ?? p?.lat;
      const lng = p?.geolocationLong ?? p?.lng;
      if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
        const key6 = `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;
        const key5 = `${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}`;
        if (coordSet6.has(key6) || coordSet5.has(key5)) return false;
      }

      return true;
    });
  }, [nearByProperties, data, nearbyListings]);

  const toggleMapModal = useCallback(() => {
    setIsOpen((prev) => {
      const newValue = !prev;
      // Resize map when drawer state changes
      if (mapRef.current && mapLoaded) {
        setTimeout(() => {
          if (mapRef.current) {
            try {
              const map = mapRef.current.getMap();
              if (map.isStyleLoaded()) {
                map.resize();
              }
            } catch (error) {
              // Silently ignore style loading errors
              if (!(error instanceof Error && error.message.includes('Style is not done loading'))) {
                console.error("Error resizing map:", error);
              }
            }
          }
        }, newValue ? 300 : 200); // Delay for drawer animation
      }
      return newValue;
    });
  }, [mapLoaded]);

  const { currency } = useCurrentCurrency();
  const [searchParams] = useSearchParams();
  const regionCurrency =
    getRegionConfigByCountryCodeRegion(region || "")?.currency || currency;
  const tierCurrencyIsoCode = useMemo(
    () =>
      resolveGlobalPriceTierCurrency(
        globalPriceTier,
        [...data, ...nearbyListings],
        regionCurrency,
      ) || regionCurrency,
    [globalPriceTier, data, nearbyListings, regionCurrency],
  );

  const [visibleTiers, setVisibleTiers] = useState<MapPinPriceTier[] | null>(
    null,
  );

  const resolvedPolygonBounds = useMemo(
    () => resolveLocationPolygonBounds(locationPolygon, locationPolygonBounds),
    [locationPolygon, locationPolygonBounds],
  );

  const resolvedPolygonBoundsKey = useMemo(() => {
    if (!resolvedPolygonBounds) return "";
    const {
      minLatitude,
      minLongitude,
      maxLatitude,
      maxLongitude,
    } = resolvedPolygonBounds;
    return `${minLatitude}:${minLongitude}:${maxLatitude}:${maxLongitude}`;
  }, [resolvedPolygonBounds]);

  const isViewportDrivenQuery = useMemo(
    () =>
      Boolean(
        parsedQuery?.minLatitude &&
        parsedQuery?.maxLatitude &&
        parsedQuery?.minLongitude &&
        parsedQuery?.maxLongitude,
      ),
    [parsedQuery?.minLatitude, parsedQuery?.maxLatitude, parsedQuery?.minLongitude, parsedQuery?.maxLongitude],
  );

  const queryViewportBounds = useMemo(
    () => parseQueryViewportBounds(parsedQuery),
    [parsedQuery?.minLatitude, parsedQuery?.maxLatitude, parsedQuery?.minLongitude, parsedQuery?.maxLongitude],
  );

  const fitMapToQueryViewport = useCallback((duration = 0) => {
    if (!mapRef.current || !queryViewportBounds) return false;

    const map = mapRef.current.getMap();
    isProgrammaticMoveRef.current = true;
    hasInitialCalibrationRef.current = true;
    map.fitBounds(
      [
        [queryViewportBounds.minLongitude, queryViewportBounds.minLatitude],
        [queryViewportBounds.maxLongitude, queryViewportBounds.maxLatitude],
      ],
      {
        padding: 0,
        duration,
      },
    );
    map.once("moveend", () => {
      updateUnclusteredPointsRef.current?.();
    });
    setTimeout(() => {
      isProgrammaticMoveRef.current = false;
    }, Math.max(duration, 0) + 100);
    return true;
  }, [queryViewportBounds]);

  const fitMapToLocationPolygon = useCallback((force = false) => {
    if (!mapRef.current || !resolvedPolygonBounds) return false;
    if (isViewportDrivenQuery && !force && !recalibrate && !pendingRecalibrateRef.current) {
      return false;
    }

    const map = mapRef.current.getMap();
    isProgrammaticMoveRef.current = true;
    hasInitialCalibrationRef.current = true;
    if (resolvedPolygonBoundsKey) {
      lastFittedPolygonBoundsKeyRef.current = resolvedPolygonBoundsKey;
    }
    map.fitBounds(
      [
        [resolvedPolygonBounds.minLongitude, resolvedPolygonBounds.minLatitude],
        [resolvedPolygonBounds.maxLongitude, resolvedPolygonBounds.maxLatitude],
      ],
      {
        padding: { top: 48, bottom: 48, left: 48, right: 48 },
        duration: 1000,
      },
    );
    map.once("moveend", () => {
      const zoom = map.getZoom();
      const refreshMarkers = () => {
        updateUnclusteredPointsRef.current?.();
      };

      if (zoom < MAP_MAX_ZOOM) {
        map.once("moveend", refreshMarkers);
        map.easeTo({
          zoom: Math.min(zoom + 0.1, MAP_MAX_ZOOM),
          duration: 300,
        });
        return;
      }

      refreshMarkers();
    });
    setTimeout(() => {
      isProgrammaticMoveRef.current = false;
    }, 1400);
    return true;
  }, [resolvedPolygonBounds, resolvedPolygonBoundsKey, isViewportDrivenQuery, recalibrate]);

  const fitMapToMarkers = useCallback((force = false) => {
    if (!mapRef.current) return;
    const map = mapRef.current.getMap();

    const searchPoint = readSearchMapPoint(region);
    const regionPoint = readRegionMapPoint(region);
    const params = query ? Object.fromEntries(new URLSearchParams(query)) : {};
    const nearbyDotCoords =
      !data.length && !nearbyListings.length
        ? (Array.isArray(nearByProperties) ? nearByProperties : [])
          .map((p: any) => {
            const lng = p?.geolocationLong ?? p?.lng;
            const lat = p?.geolocationLat ?? p?.lat;
            if (!Number.isFinite(Number(lng)) || !Number.isFinite(Number(lat))) return null;
            return [Number(lng), Number(lat)] as [number, number];
          })
          .filter(Boolean) as [number, number][]
        : [];

    const coordinates: [number, number][] = data.length
      ? data.map((p) => [p.geolocationLong, p.geolocationLat] as [number, number])
      : nearbyListings.length
        ? nearbyListings.map((p) => [p.geolocationLong, p.geolocationLat] as [number, number])
        : nearbyDotCoords.length
          ? nearbyDotCoords
          : (params.longitude && params.latitude)
            ? [[parseFloat(String(params.longitude)), parseFloat(String(params.latitude))] as [number, number]]
            : searchPoint
              ? [[searchPoint.longitude, searchPoint.latitude] as [number, number]]
              : regionPoint
                ? [[regionPoint.longitude, regionPoint.latitude] as [number, number]]
                : [];

    if (!coordinates.length) return;

    if (coordinates.length === 1) {
      // If only one marker, center it with a default zoom
      // Mark as programmatic move to prevent API call
      isProgrammaticMoveRef.current = true;
      hasInitialCalibrationRef.current = true;
      map.flyTo({
        center: coordinates[0],
        zoom: 10,
      });
      // Reset flag after animation completes
      setTimeout(() => {
        isProgrammaticMoveRef.current = false;
      }, 1100);
      return;
    }
    // Calculate the bounding box
    const bounds = coordinates.reduce(
      (acc, coord) => {
        return [
          [Math.min(acc[0][0], coord[0]), Math.min(acc[0][1], coord[1])],
          [Math.max(acc[1][0], coord[0]), Math.max(acc[1][1], coord[1])],
        ];
      },
      [
        [Infinity, Infinity], // min lng, min lat
        [-Infinity, -Infinity], // max lng, max lat
      ],
    );

    // Don't auto-fit for map viewport driven searches after initial load.
    const isViewportDrivenQuery = Boolean(
      params.minLatitude && params.maxLatitude && params.minLongitude && params.maxLongitude,
    );
    const shouldFitMap = force || recalibrate || !isViewportDrivenQuery;
    if (shouldFitMap) {
      // Mark as programmatic move to prevent API call
      isProgrammaticMoveRef.current = true;
      hasInitialCalibrationRef.current = true;
      // Fit the map to the bounds
      map.fitBounds(bounds, {
        padding: 100, // Adjust padding for better fit
        duration: 1000, // Smooth animation
      });
      updateMapOnResize?.();
      // Reset flag after animation completes
      setTimeout(() => {
        isProgrammaticMoveRef.current = false;
      }, 1100);
    }
  }, [data, query, parsedQuery, region, showSearch, recalibrate, nearbyListings, nearByProperties]);

  const filterQueryKey = useMemo(() => buildFilterQueryKey(query), [query]);
  // Place / admin location only — do not include map viewport lat/lng.
  // "Search this area" updates those coords on every drag and must not reset
  // the tier legend / priceTiers filter.
  const locationContextKey = useMemo(
    () =>
      [
        parsedQuery?.placeId,
        parsedQuery?.text,
        parsedQuery?.city,
        parsedQuery?.state,
        parsedQuery?.country,
      ]
        .map((value) => String(value ?? ""))
        .join("|"),
    [
      parsedQuery?.placeId,
      parsedQuery?.text,
      parsedQuery?.city,
      parsedQuery?.state,
      parsedQuery?.country,
    ],
  );
  const prevLocationContextKeyRef = useRef<string | null>(null);
  /** Previous available tiers — used so user deselections are not treated as "new" tiers. */
  const prevMapPriceTiersPresentRef = useRef<MapPinPriceTier[]>([]);
  const mapPinIdsKey = useMemo(() => {
    const ids = collectMapPinIds([...data, ...nearbyListings]);
    return [...ids].sort().join(",");
  }, [data, nearbyListings]);
  const mapPriceTiersPresent = useMemo(() => {
    const fromApi = getGlobalPriceTierPresent(globalPriceTier);
    if (fromApi.length) return fromApi;

    const tiers = new Set<MapPinPriceTier>();
    [...data, ...nearbyListings].forEach((pin) => {
      const tier = resolvePriceTier(pin);
      if (tier) tiers.add(tier);
    });
    return MAP_PIN_TIER_ORDER.filter((tier) => tiers.has(tier));
  }, [globalPriceTier, data, nearbyListings]);

  const tierStartingPrices = useMemo(() => {
    // Prefer API band minCost — priced in property `currencyIsoCode` (e.g. AUD).
    if (globalPriceTier) {
      const fromApi: Partial<
        Record<MapPinPriceTier, { price: number; currencyIsoCode?: string }>
      > = {};
      const silver = Number(globalPriceTier.silver?.minCost);
      const gold = Number(globalPriceTier.gold?.minCost);
      const platinum = Number(globalPriceTier.platinum?.minCost);
      if (Number.isFinite(silver) && silver > 0) {
        fromApi.Silver = {
          price: silver,
          currencyIsoCode: tierCurrencyIsoCode,
        };
      }
      if (Number.isFinite(gold) && gold > 0) {
        fromApi.Gold = { price: gold, currencyIsoCode: tierCurrencyIsoCode };
      }
      if (Number.isFinite(platinum) && platinum > 0) {
        fromApi.Platinum = {
          price: platinum,
          currencyIsoCode: tierCurrencyIsoCode,
        };
      }
      if (Object.keys(fromApi).length) return fromApi;
    }

    // Fallback: derive mins from visible pins.
    const mins: Partial<
      Record<MapPinPriceTier, { price: number; currencyIsoCode: string; comparable: number }>
    > = {};

    [...data, ...nearbyListings].forEach((pin) => {
      const tier = resolvePriceTier(pin);
      const price = resolvePinMonthlyPrice(pin, showPerDesk);
      if (!tier || price == null) return;

      const currencyIsoCode = pin?.currencyIsoCode || tierCurrencyIsoCode;
      const comparable = toComparablePrice(price, currencyIsoCode, currency);
      if (comparable == null) return;

      const existing = mins[tier];
      if (!existing || comparable < existing.comparable) {
        mins[tier] = { price, currencyIsoCode, comparable };
      }
    });

    return Object.fromEntries(
      Object.entries(mins).map(([tier, value]) => [
        tier,
        { price: value.price, currencyIsoCode: value.currencyIsoCode },
      ]),
    ) as Partial<Record<MapPinPriceTier, { price: number; currencyIsoCode?: string }>>;
  }, [
    globalPriceTier,
    data,
    nearbyListings,
    showPerDesk,
    currency,
    tierCurrencyIsoCode,
  ]);

  const applyTierPriceFilter = useCallback(
    (
      nextSelected: MapPinPriceTier[],
      options?: { fromUserToggle?: boolean },
    ) => {
      if (!callGeoSearch || !mapPriceTiersPresent.length) return;

      const available = mapPriceTiersPresent;
      const selected = MAP_PIN_TIER_ORDER.filter(
        (tier) => available.includes(tier) && nextSelected.includes(tier),
      );
      const allSelected =
        selected.length > 0 && available.every((tier) => selected.includes(tier));

      const currentMin =
        searchParams.get("minPrice") ||
        (parsedQuery?.minPrice ? String(parsedQuery.minPrice) : "");
      const currentMax =
        searchParams.get("maxPrice") ||
        (parsedQuery?.maxPrice ? String(parsedQuery.maxPrice) : "");
      const currentSortBy =
        searchParams.get("sortBy") ||
        (parsedQuery?.sortBy ? String(parsedQuery.sortBy) : "");
      // Loader `data.query` uses `sortOrder`; browser URL uses `order`.
      const currentOrder =
        searchParams.get("order") ||
        (parsedQuery?.order
          ? String(parsedQuery.order)
          : parsedQuery?.sortOrder
            ? String(parsedQuery.sortOrder)
            : "");
      const currentPriceTiers = searchParams.get("priceTiers") || "";

      // Automatic sync (e.g. location change) must not wipe a Price drawer /
      // searchbar range. Legend-driven filters use priceTiers and/or
      // sortBy=price&order=asc; drawer/searchbar typically uses best_match.
      const isTierDrivenPriceFilter =
        !!currentPriceTiers ||
        (currentSortBy === "price" && currentOrder === "asc");
      if (
        !options?.fromUserToggle &&
        !isTierDrivenPriceFilter &&
        (currentMin || currentMax)
      ) {
        return;
      }

      if (selected.length === 0) {
        if (
          !currentMin &&
          !currentMax &&
          currentPriceTiers === "none"
        ) {
          return;
        }
        callGeoSearch({
          minPrice: "reset",
          maxPrice: "reset",
          priceTiers: "none",
          sortBy: "price",
          order: "asc",
          page: "1",
        });
        return;
      }

      if (allSelected) {
        if (!currentMin && !currentMax && !currentPriceTiers) return;
        callGeoSearch({
          minPrice: "reset",
          maxPrice: "reset",
          priceTiers: "reset",
          page: "1",
        });
        return;
      }

      const range = getSelectedTiersPriceFilter(
        globalPriceTier,
        selected,
        available,
      );
      const nextPriceTiers = selected.join(",");

      // Always persist the exact checkbox selection. Price bounds are best-effort
      // for listings API when globalPriceTier is available.
      const nextMin = range
        ? convertCurrency(
          range.minPrice,
          tierCurrencyIsoCode,
          currency,
          true,
        )
        : "";
      const nextMax =
        range?.maxPrice != null
          ? convertCurrency(
            range.maxPrice,
            tierCurrencyIsoCode,
            currency,
            true,
          )
          : "";

      const sortAlreadyPriceAsc =
        currentSortBy === "price" && currentOrder === "asc";
      if (
        (nextMin || "") === currentMin &&
        (nextMax || "") === currentMax &&
        nextPriceTiers === currentPriceTiers &&
        sortAlreadyPriceAsc
      ) {
        return;
      }

      callGeoSearch({
        minPrice: nextMin || "reset",
        maxPrice: nextMax || "reset",
        priceTiers: nextPriceTiers,
        sortBy: "price",
        order: "asc",
        page: "1",
      });
    },
    [
      callGeoSearch,
      globalPriceTier,
      mapPriceTiersPresent,
      parsedQuery?.minPrice,
      parsedQuery?.maxPrice,
      parsedQuery?.sortBy,
      parsedQuery?.order,
      parsedQuery?.sortOrder,
      searchParams,
      tierCurrencyIsoCode,
      currency,
    ],
  );

  const urlDerivedSelectedTiers = useMemo(() => {
    const priceTiersParam = searchParams.get("priceTiers");
    if (priceTiersParam === "none") {
      return [] as MapPinPriceTier[];
    }
    if (priceTiersParam) {
      const parsed = priceTiersParam
        .split(",")
        .map((tier) => tier.trim())
        .filter((tier): tier is MapPinPriceTier =>
          MAP_PIN_TIER_ORDER.includes(tier as MapPinPriceTier),
        );
      // Prefer the explicit legend param even before available-tiers hydrate.
      // Do not fall through to minPrice derivation (that re-selects higher tiers).
      if (parsed.length) {
        const available = new Set(mapPriceTiersPresent);
        const filtered = available.size
          ? parsed.filter((tier) => available.has(tier))
          : parsed;
        return MAP_PIN_TIER_ORDER.filter((tier) => filtered.includes(tier));
      }
      return null;
    }

    const currentSortBy = parsedQuery?.sortBy
      ? String(parsedQuery.sortBy)
      : searchParams.get("sortBy") || "";
    // Loader `data.query` uses `sortOrder`; browser URL uses `order`.
    const currentOrder = parsedQuery?.order
      ? String(parsedQuery.order)
      : parsedQuery?.sortOrder
        ? String(parsedQuery.sortOrder)
        : searchParams.get("order") || "";
    const currentMaxRaw =
      searchParams.get("maxPrice") ||
      (parsedQuery?.maxPrice ? String(parsedQuery.maxPrice) : "");
    const currentMinRaw =
      searchParams.get("minPrice") ||
      (parsedQuery?.minPrice ? String(parsedQuery.minPrice) : "");
    const currentMinNum = currentMinRaw ? Number(currentMinRaw) : NaN;
    const currentMaxNum = currentMaxRaw ? Number(currentMaxRaw) : NaN;

    // Fallback for legacy tier-driven URLs without `priceTiers`.
    const isTierDriven =
      currentSortBy === "price" && currentOrder === "asc";
    const legacyMinOnly =
      !currentSortBy &&
      !currentMaxRaw &&
      Number.isFinite(currentMinNum) &&
      currentMinNum > 0;
    if (!isTierDriven && !legacyMinOnly) return null;
    if (!Number.isFinite(currentMinNum) || currentMinNum <= 0) return null;

    return getSelectedTiersFromPriceRange(
      globalPriceTier,
      mapPriceTiersPresent,
      currentMinNum,
      Number.isFinite(currentMaxNum) && currentMaxNum > 0
        ? currentMaxNum
        : null,
      (bandMinCost) => {
        const converted = convertCurrency(
          bandMinCost,
          tierCurrencyIsoCode,
          currency,
          true,
        );
        const n = Number(converted);
        return Number.isFinite(n) ? n : null;
      },
    );
  }, [
    globalPriceTier,
    mapPriceTiersPresent,
    parsedQuery?.minPrice,
    parsedQuery?.maxPrice,
    parsedQuery?.sortBy,
    parsedQuery?.order,
    parsedQuery?.sortOrder,
    searchParams,
    tierCurrencyIsoCode,
    currency,
  ]);

  // `null` = not hydrated yet (remount). `[]` = user unchecked every tier.
  // Prefer local selection after hydration so in-flight URL updates cannot
  // re-check tiers the user just deselected.
  const cachedTiers =
    mapPinTierSelectionCache?.locationKey === locationContextKey
      ? mapPinTierSelectionCache.tiers
      : null;
  const priceTiersParam = searchParams.get("priceTiers");
  const effectiveSelectedTiers =
    visibleTiers ??
    // Only restore a partial legend selection when `priceTiers` is in the URL.
    // Manual price filters clear that param and should show every tier checked.
    (priceTiersParam ? urlDerivedSelectedTiers : null) ??
    (priceTiersParam ? cachedTiers : null) ??
    mapPriceTiersPresent;

  // Keep selection in sync with available tiers; reset to all when location changes.
  // Hydrate from cache / priceTiers URL only while local state is null.
  const tierPriceFilterKey = [
    searchParams.get("minPrice") ?? parsedQuery?.minPrice ?? "",
    searchParams.get("maxPrice") ?? parsedQuery?.maxPrice ?? "",
    searchParams.get("sortBy") ?? parsedQuery?.sortBy ?? "",
    searchParams.get("order") ??
    parsedQuery?.order ??
    parsedQuery?.sortOrder ??
    "",
    searchParams.get("priceTiers") ?? "",
  ].join("|");
  const prevTierPriceFilterKeyRef = useRef(tierPriceFilterKey);

  useEffect(() => {
    const prevLocation = prevLocationContextKeyRef.current;
    const locationChanged =
      prevLocation !== null && prevLocation !== locationContextKey;
    prevLocationContextKeyRef.current = locationContextKey;

    const prevPresent = prevMapPriceTiersPresentRef.current;
    prevMapPriceTiersPresentRef.current = mapPriceTiersPresent;

    const priceFilterChanged =
      prevTierPriceFilterKeyRef.current !== tierPriceFilterKey;
    prevTierPriceFilterKeyRef.current = tierPriceFilterKey;

    const currentPriceTiersParam = searchParams.get("priceTiers");

    const resolveLocationTiers = (): MapPinPriceTier[] => {
      // Keep an explicit legend selection across placeId/text clears from map
      // drag ("Search this area") and other location-context updates.
      if (currentPriceTiersParam === "none") return [];
      if (currentPriceTiersParam && urlDerivedSelectedTiers?.length) {
        const kept = urlDerivedSelectedTiers.filter((tier) =>
          mapPriceTiersPresent.includes(tier),
        );
        return kept.length ? kept : mapPriceTiersPresent;
      }
      if (currentPriceTiersParam && cachedTiers?.length) {
        const kept = cachedTiers.filter((tier) =>
          mapPriceTiersPresent.includes(tier),
        );
        return kept.length ? kept : mapPriceTiersPresent;
      }
      return mapPriceTiersPresent;
    };

    setVisibleTiers((prev) => {
      const next = (() => {
        if (locationChanged) {
          const resolved = resolveLocationTiers();
          if (!currentPriceTiersParam) {
            mapPinTierSelectionCache = null;
          }
          return resolved;
        }

        // Remount / first paint: prefer URL when present, else in-memory cache.
        if (prev == null) {
          if (currentPriceTiersParam && urlDerivedSelectedTiers) {
            return urlDerivedSelectedTiers;
          }
          if (currentPriceTiersParam && cachedTiers) {
            return cachedTiers;
          }
          mapPinTierSelectionCache = null;
          return mapPriceTiersPresent;
        }

        // Manual price filter (searchbar/drawer) clears `priceTiers` from the URL.
        // Re-check every legend tier so pin filters match the manual range.
        // `priceTiers=none` is an intentional empty legend selection — keep it.
        if (priceFilterChanged && !currentPriceTiersParam) {
          mapPinTierSelectionCache = null;
          return mapPriceTiersPresent;
        }

        const kept = prev.filter((tier) => mapPriceTiersPresent.includes(tier));
        if (!kept.length) {
          // Keep an intentional empty selection; only fall back when the market
          // no longer has any of the previously selected tiers.
          return prev.length ? prev : mapPriceTiersPresent;
        }

        // Only auto-include tiers that are newly available in the market —
        // not tiers the user has intentionally deselected.
        // Skip when prevPresent is empty (initial/reload) or we would re-add
        // every tier and wipe deselections after filter navigation.
        const newlyAvailable =
          prevPresent.length > 0
            ? mapPriceTiersPresent.filter(
              (tier) => !prevPresent.includes(tier),
            )
            : [];
        if (newlyAvailable.length) {
          const merged = new Set([...kept, ...newlyAvailable]);
          return MAP_PIN_TIER_ORDER.filter((tier) => merged.has(tier));
        }

        return kept;
      })();

      // `.filter()` always returns a new array. Reusing `prev` when contents
      // match stops a setState loop if this effect's deps are unstable.
      return samePriceTiers(prev, next) ? prev : next;
    });

    if (locationChanged) {
      // Do not wipe a user-selected tier filter when the map viewport moves.
      // Re-sync only when there is no explicit priceTiers selection.
      if (!currentPriceTiersParam) {
        applyTierPriceFilter(mapPriceTiersPresent);
      } else if (currentPriceTiersParam === "none") {
        applyTierPriceFilter([]);
      } else {
        applyTierPriceFilter(resolveLocationTiers());
      }
    }
  }, [
    locationContextKey,
    mapPriceTiersPresent,
    applyTierPriceFilter,
    tierPriceFilterKey,
    urlDerivedSelectedTiers,
    parsedQuery?.minPrice,
    parsedQuery?.maxPrice,
    searchParams,
    cachedTiers,
  ]);

  const filteredPins = useMemo(() => {
    const selected = new Set(effectiveSelectedTiers);
    return [...data, ...nearbyListings].filter((pin) => {
      const tier = resolvePriceTier(pin);
      // Pins without a tier are not controlled by the legend (always visible).
      if (!tier) return true;
      return selected.has(tier);
    });
  }, [data, nearbyListings, effectiveSelectedTiers]);

  const pinCount = filteredPins.length;

  const mapVisiblePinIdsKey = useMemo(() => {
    const ids = collectMapPinIds(filteredPins);
    return [...ids].sort().join(",");
  }, [filteredPins]);

  const showPinTierLegend =
    markerVariant === "teal-pin" && mapPriceTiersPresent.length > 0;
  const clusterMaxZoom = useMemo(() => getClusterMaxZoom(pinCount), [pinCount]);

  const clusterData: GeoJSONData = useMemo(() => {
    return {
      type: "FeatureCollection",
      features: filteredPins
        .filter(isValidMapPin)
        .map((p) => {
          const priceTier = resolvePriceTier(p);
          return {
            type: "Feature",
            properties: {
              title: "Point",
              priceTier: priceTier ?? "",
            },
            geometry: {
              type: "Point",
              coordinates: [Number(p.geolocationLong), Number(p.geolocationLat)] as [number, number],
            },
            data: { ...p, priceTier: priceTier ?? p.priceTier ?? null },
          };
        }),
    };
  }, [filteredPins]);

  const updateMapOnResize = useCallback(
    debounce(() => {
      if (!mapRef.current) return;
      const map = mapRef.current.getMap();
      map.resize(); // ✅ Resize Mapbox instance
    }, 50), // ✅ Delays execution by 50ms to avoid flickering
    [mapCenter],
  );

  useEffect(() => {
    if (!mapRef.current || !rightContainerRef.current || data.length === 0)
      return;

    const observer = new ResizeObserver(updateMapOnResize);
    observer.observe(rightContainerRef.current);

    return () => {
      observer.disconnect();
      updateMapOnResize.cancel(); // Clean up debounce function
    };
  }, [rightWidth, updateMapOnResize]);

  const groupPointsByLocation = useCallback((points: any) => {
    const grouped: Record<string, Feature & { count: number; data: any[] }> = {};

    points.forEach((point: Feature & { count: number; data: any[] }) => {
      const coordKey = `${point.geometry.coordinates[0]},${point.geometry.coordinates[1]}`;

      if (!grouped[coordKey]) {
        grouped[coordKey] = { ...point, data: [point], count: 1 };
      } else {
        grouped[coordKey].count += 1;
        grouped[coordKey].data.push(point);
      }
    });

    return Object.values(grouped);
  }, []);

  // Used to prevent setState loops when parent props (e.g. parsedQuery) are re-created each render.
  const unclusteredHashRef = useRef<string>('');
  const visibleClustersHashRef = useRef<string>('');
  const superclusterRef = useRef<supercluster | null>(null);

  const updateUnclusteredPoints = useCallback(() => {
    if (!mapRef.current) return;

    const clearMapMarkers = () => {
      if (unclusteredHashRef.current !== "") {
        unclusteredHashRef.current = "";
        setUnclusteredPoints([]);
      }
      if (visibleClustersHashRef.current !== "") {
        visibleClustersHashRef.current = "";
        setVisibleClusters([]);
      }
      superclusterRef.current = null;
    };

    if (!clusterData.features.length) {
      clearMapMarkers();
      return;
    }

    const map = mapRef.current.getMap();
    const zoom = map.getZoom();
    const zoomLevel = Math.floor(zoom);
    const clusterRadius = getClusterRadiusForZoom(zoom, pinCount);

    const clusterDisplayMaxZoom = clusterMaxZoom;
    const showAsClusters = zoomLevel <= clusterDisplayMaxZoom;
    const clusterViewKey = `${zoomLevel}:${showAsClusters ? "cluster" : "point"}:`;

    const commitGroupedPoints = (grouped: ReturnType<typeof groupPointsByLocation>) => {
      const nextHash = `${clusterViewKey}${grouped
        .map((p: any) => `${p.geometry.coordinates?.[0]},${p.geometry.coordinates?.[1]}:${p.count ?? 1}`)
        .sort()
        .join("|")}`;
      if (nextHash === unclusteredHashRef.current) return;
      unclusteredHashRef.current = nextHash;
      setUnclusteredPoints(grouped);
    };

    // Single pin — no supercluster needed.
    if (pinCount < MAP_MIN_CLUSTER_PIN_COUNT) {
      if (visibleClustersHashRef.current !== "") {
        visibleClustersHashRef.current = "";
        setVisibleClusters([]);
      }
      commitGroupedPoints(groupPointsByLocation(clusterData.features));
      return;
    }

    const bounds = map.getBounds().toArray().flat() as any;
    const expandFactor = 0.5;
    const minLng = bounds[0] - (bounds[2] - bounds[0]) * expandFactor;
    const minLat = bounds[1] - (bounds[3] - bounds[1]) * expandFactor;
    const maxLng = bounds[2] + (bounds[2] - bounds[0]) * expandFactor;
    const maxLat = bounds[3] + (bounds[3] - bounds[1]) * expandFactor;
    const expandedBounds = [minLng, minLat, maxLng, maxLat] as any;

    const clusterIndexMaxZoom = clusterMaxZoom;
    const clusterIndex = new supercluster({ radius: clusterRadius, maxZoom: clusterIndexMaxZoom });
    clusterIndex.load(clusterData.features);
    superclusterRef.current = clusterIndex;

    // When showing individual pins, query above maxZoom so supercluster returns leaves.
    // Otherwise zoom levels like 12.4 use floor=12 (clustered) while showAsClusters is false — pins vanish.
    const queryZoom = showAsClusters
      ? zoomLevel
      : Math.max(zoomLevel, clusterIndexMaxZoom + 1);
    const features = clusterIndex.getClusters(expandedBounds, queryZoom);
    const clusterFeatures = features.filter((feature: any) => feature.properties?.cluster);
    const individualMarkers = features.filter((feature: any) => !feature.properties?.cluster);

    if (showAsClusters) {
      const clusterHash = `${clusterViewKey}${clusterFeatures
        .map((feature: any) => {
          const [lng, lat] = feature.geometry.coordinates;
          return `${lng},${lat}:${feature.properties?.point_count ?? 0}`;
        })
        .sort()
        .join("|")}`;
      if (clusterHash !== visibleClustersHashRef.current) {
        visibleClustersHashRef.current = clusterHash;
        setVisibleClusters(clusterFeatures);
      }
      if (individualMarkers.length) {
        commitGroupedPoints(groupPointsByLocation(individualMarkers));
      } else if (unclusteredHashRef.current !== "") {
        unclusteredHashRef.current = "";
        setUnclusteredPoints([]);
      }
      return;
    }

    if (visibleClustersHashRef.current !== "") {
      visibleClustersHashRef.current = "";
      setVisibleClusters([]);
    }
    commitGroupedPoints(groupPointsByLocation(individualMarkers));
  }, [clusterData, clusterMaxZoom, groupPointsByLocation, pinCount]);

  useEffect(() => {
    updateUnclusteredPointsRef.current = updateUnclusteredPoints;
  }, [updateUnclusteredPoints]);

  const clusterClickLockRef = useRef(false);
  const clusterMaxZoomRef = useRef(clusterMaxZoom);
  useEffect(() => {
    clusterMaxZoomRef.current = clusterMaxZoom;
  }, [clusterMaxZoom]);

  const resolveClusterPriceTier = useCallback((clusterFeature: any): MapPinPriceTier | null => {
    const clusterIndex = superclusterRef.current;
    const clusterId = clusterFeature?.properties?.cluster_id;
    if (!clusterIndex || clusterId == null) return null;

    try {
      const leaves = clusterIndex.getLeaves(clusterId, 100);
      return resolveLowestPriceTier(
        leaves.map((leaf: any) => resolvePriceTier(leaf.data ?? leaf.properties)),
      );
    } catch {
      return null;
    }
  }, []);

  const hoveredClusterIds = useMemo(() => {
    if (!activeHoverIds.length) return new Set<number>();

    const clusterIndex = superclusterRef.current;
    if (!clusterIndex || !visibleClusters.length) return new Set<number>();

    const ids = new Set<number>();
    visibleClusters.forEach((clusterFeature) => {
      const clusterId = clusterFeature?.properties?.cluster_id;
      if (clusterId == null) return;

      try {
        const leaves = clusterIndex.getLeaves(clusterId, Infinity);
        const containsHoveredPin = leaves.some((leaf: any) =>
          matchesMapPinHover(resolveFeaturePinRecord(leaf), activeHoverIds),
        );
        if (containsHoveredPin) ids.add(clusterId);
      } catch {
        // Ignore invalid cluster ids during index rebuilds.
      }
    });

    return ids;
  }, [activeHoverIds, visibleClusters]);

  const handleClusterBubbleClick = useCallback((clusterFeature: any) => {
    if (!mapRef.current || clusterClickLockRef.current) return;

    const clusterIndex = superclusterRef.current;
    const clusterId = clusterFeature?.properties?.cluster_id;
    if (!clusterIndex || clusterId == null) return;

    const map = mapRef.current.getMap();
    const clusterCenter = clusterFeature.geometry.coordinates as [number, number];
    const currentZoom = map.getZoom();
    const maxClusterZoom = clusterMaxZoomRef.current;

    clusterClickLockRef.current = true;
    isProgrammaticMoveRef.current = true;

    try {
      const expansionZoom = Math.min(
        clusterIndex.getClusterExpansionZoom(clusterId),
        MAP_MAX_ZOOM,
      );
      const clusterZoomStep = 2;
      let targetZoom: number;

      if (expansionZoom <= currentZoom + clusterZoomStep) {
        // Close to the next split — finish expanding this cluster.
        targetZoom = expansionZoom;
      } else if (expansionZoom <= maxClusterZoom) {
        // Still within cluster mode — advance one step at a time.
        targetZoom = currentZoom + clusterZoomStep;
      } else if (currentZoom < maxClusterZoom) {
        // Cluster only breaks past clusterMaxZoom — step toward pin mode.
        targetZoom = Math.min(currentZoom + clusterZoomStep, maxClusterZoom);
      } else {
        // Already past clusterMaxZoom — step toward individual pins.
        targetZoom = Math.min(currentZoom + clusterZoomStep, MAP_MAX_ZOOM);
      }

      targetZoom = Math.min(Math.max(targetZoom, currentZoom + 1), MAP_MAX_ZOOM);

      map.once("moveend", () => {
        updateUnclusteredPointsRef.current?.();
        clusterClickLockRef.current = false;
        setTimeout(() => {
          isProgrammaticMoveRef.current = false;
        }, 100);
      });

      map.easeTo({
        center: clusterCenter,
        zoom: targetZoom,
        duration: 600,
      });
    } catch {
      clusterClickLockRef.current = false;
      isProgrammaticMoveRef.current = false;
    }
  }, []);

  const runRecalibration = useCallback(() => {
    if (!mapRef.current || !mapLoaded) return false;
    setShowSearch(false);
    hasUserInteractedRef.current = false;
    const fittedToQueryViewport = fitMapToQueryViewport(1000);
    if (!fittedToQueryViewport) {
      const fittedToPolygon = fitMapToLocationPolygon(true);
      if (!fittedToPolygon) {
        lastFittedPolygonBoundsKeyRef.current = "";
        fitMapToMarkers(true);
      }
    }
    updateUnclusteredPoints();
    return true;
  }, [fitMapToLocationPolygon, fitMapToMarkers, fitMapToQueryViewport, mapLoaded, updateUnclusteredPoints]);

  // Mark that a loader/revalidation cycle started while a recalibrate is pending,
  // so we wait for fresh polygon/listing data before fitting the camera.
  useEffect(() => {
    if (pendingRecalibrateRef.current && loading) {
      sawLoadingWhilePendingRecalibrateRef.current = true;
    }
  }, [loading]);

  useEffect(() => {
    if (!recalibrate) return;
    pendingRecalibrateRef.current = true;
    sawLoadingWhilePendingRecalibrateRef.current = false;
    // Snapshot bounds at the moment recalibration was requested (not on later updates).
    pendingRecalibrateBaselineBoundsKeyRef.current = resolvedPolygonBoundsKey;
    setShowSearch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-snapshot when recalibrate is requested
  }, [recalibrate]);

  useEffect(() => {
    if (!pendingRecalibrateRef.current || loading || !mapLoaded) return;

    const boundsChanged =
      resolvedPolygonBoundsKey !== pendingRecalibrateBaselineBoundsKeyRef.current;
    const finishedLoadCycle = sawLoadingWhilePendingRecalibrateRef.current;

    // Same-URL address searches revalidate after setRecalibrate(true). Avoid
    // fitting the previous polygon on the first idle frame before loading flips.
    if (!boundsChanged && !finishedLoadCycle) {
      const timeoutId = window.setTimeout(() => {
        if (!pendingRecalibrateRef.current || loadingRef.current) return;
        // No fetch started (e.g. in-memory recalibrate) — fit current data.
        if (
          !sawLoadingWhilePendingRecalibrateRef.current &&
          runRecalibration()
        ) {
          pendingRecalibrateRef.current = false;
          pendingRecalibrateBaselineBoundsKeyRef.current = null;
          setRecalibrate?.(false);
        }
      }, 0);
      return () => window.clearTimeout(timeoutId);
    }

    if (runRecalibration()) {
      pendingRecalibrateRef.current = false;
      sawLoadingWhilePendingRecalibrateRef.current = false;
      pendingRecalibrateBaselineBoundsKeyRef.current = null;
      setRecalibrate?.(false);
    }
  }, [
    recalibrate,
    loading,
    mapLoaded,
    data,
    parsedQuery?.latitude,
    parsedQuery?.longitude,
    parsedQuery?.placeId,
    parsedQuery?.text,
    locationPolygon,
    resolvedPolygonBounds,
    resolvedPolygonBoundsKey,
    runRecalibration,
    setRecalibrate,
  ]);

  const handleGeoSearch = useCallback(() => {
    if (!mapRef.current || !callGeoSearch) return;
    clearOpenMarkerPopup();
    const map = mapRef.current.getMap();
    const bounds = map.getBounds();

    const minLongitude = bounds.getWest();
    const minLatitude = bounds.getSouth();
    const maxLongitude = bounds.getEast();
    const maxLatitude = bounds.getNorth();
    const center = map.getCenter();

    if (minLongitude <= -180 || maxLongitude >= 180) {
      return;
    }

    callGeoSearch({
      minLatitude,
      maxLatitude,
      minLongitude,
      maxLongitude,
      latitude: center.lat,
      longitude: center.lng,
      placeId: "reset",
      text: "reset",
      page: 1,
    });
    setShowSearch(false);
    setShouldPulseSearchHere(false);
    setSearchAsIMove(true);
  }, [callGeoSearch, clearOpenMarkerPopup]);

  const getPopupPosition = useCallback((lng: number, lat: number): {
    anchor: "top" | "bottom";
    offset: [number, number];
  } => {
    const popupAboveOffset = -(PROPERTY_PIN_SIZE + MAP_POPUP_PIN_GAP);
    const popupBelowOffset = MAP_POPUP_PIN_GAP;

    if (!mapRef.current) {
      return { anchor: "bottom", offset: [0, popupAboveOffset] };
    }

    const map = mapRef.current.getMap();
    const container = map.getContainer();
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;
    const point = map.project([lng, lat]);

    let anchor: "top" | "bottom" = "bottom";
    let offsetY = popupAboveOffset;
    let offsetX = 0;

    const popupTopWhenAbove = point.y + offsetY - MAP_POPUP_HEIGHT;
    if (popupTopWhenAbove < MAP_POPUP_EDGE_PADDING) {
      anchor = "top";
      offsetY = popupBelowOffset;
    }

    const popupHalfWidth = MAP_POPUP_WIDTH / 2;
    const popupLeft = point.x + offsetX - popupHalfWidth;
    const popupRight = point.x + offsetX + popupHalfWidth;

    if (popupRight > containerWidth - MAP_POPUP_EDGE_PADDING) {
      offsetX -= popupRight - (containerWidth - MAP_POPUP_EDGE_PADDING);
    } else if (popupLeft < MAP_POPUP_EDGE_PADDING) {
      offsetX += MAP_POPUP_EDGE_PADDING - popupLeft;
    }

    if (anchor === "top") {
      const popupBottomWhenBelow = point.y + offsetY + MAP_POPUP_HEIGHT;
      if (popupBottomWhenBelow > containerHeight - MAP_POPUP_EDGE_PADDING) {
        anchor = "bottom";
        offsetY = popupAboveOffset;
      }
    }

    return { anchor, offset: [offsetX, offsetY] };
  }, []);

  const mergePropertyDetails = (property: any, details: any, propId: string) => ({
    ...(property || {}),
    ...(details || {}),
    id: details?.id || property?.id || propId,
    propertyId: details?.propertyId || property?.propertyId || propId,
    propertyType__c:
      details?.propertyType__c ||
      details?.propertytype__c ||
      property?.propertyType__c ||
      property?.propertytype__c ||
      details?.spaceTypeFormula ||
      property?.spaceTypeFormula ||
      "",
    propertytype__c:
      details?.propertytype__c ||
      details?.propertyType__c ||
      property?.propertytype__c ||
      property?.propertyType__c ||
      details?.spaceTypeFormula ||
      property?.spaceTypeFormula ||
      "",
    spaceTypeFormula:
      details?.spaceTypeFormula ||
      property?.spaceTypeFormula ||
      details?.propertyType__c ||
      property?.propertyType__c ||
      "",
    geolocationLat: details?.geolocationLat ?? property.geolocationLat ?? property.lat,
    geolocationLong: details?.geolocationLong ?? property.geolocationLong ?? property.lng,
    avgMonthlyCostPerWorkstation:
      details?.avgMonthlyCostPerWorkstation ?? property.avgMonthlyCostPerWorkstation ?? null,
    propertyOpportunityStage: details?.propertyOpportunityStage ?? property.propertyOpportunityStage,
  });

  const openPropertyDetailsPopup = useCallback(async (property: any) => {
    if (!mapRef.current) return;
    const requestId = popupRequestIdRef.current;
    setCurrentMarker(null);
    setCurrentNearbyProperty(null);

    const coords = resolvePinCoordinates(property);
    if (coords) {
      setActivePopupCoords(coords);
    }

    const loadLat = property?.geolocationLat ?? property?.lat;
    const loadLng = property?.geolocationLong ?? property?.lng;
    if (loadLat && loadLng) {
      setNearbyLoading({ lat: loadLat, lng: loadLng });
    }

    try {
      const propId = property.id || property.propertyId || property.sfid;
      const filtersQuery = buildPropertyDetailsFilterQuery(query);
      const details = await PropertiesService.getPropertyDetailsByID(propId, filtersQuery);
      if (requestId !== popupRequestIdRef.current) return;
      const merged = mergePropertyDetails(property, details, propId);
      setCurrentNearbyProperty(merged);
      onHover && onHover(merged.id || merged.propertyId || null);
    } catch {
      if (requestId !== popupRequestIdRef.current) return;
      setCurrentNearbyProperty(property);
      onHover && onHover(property.id || property.propertyId || null);
    } finally {
      if (requestId === popupRequestIdRef.current) {
        setNearbyLoading(null);
      }
    }
  }, [onHover, query]);

  const openGroupedPropertyDetailsPopup = useCallback(async (feature: Feature) => {
    if (!mapRef.current) return;
    const requestId = popupRequestIdRef.current;
    setCurrentNearbyProperty(null);

    const coords =
      resolvePinCoordinates(feature.data[0]?.data) ??
      resolvePinCoordinates({ geometry: feature.data[0]?.geometry });
    if (coords) {
      setActivePopupCoords(coords);
    }

    const firstProperty = feature.data[0]?.data;
    const loadLat = firstProperty?.geolocationLat ?? firstProperty?.lat;
    const loadLng = firstProperty?.geolocationLong ?? firstProperty?.lng;
    if (loadLat && loadLng) {
      setNearbyLoading({ lat: loadLat, lng: loadLng });
    }

    try {
      const filtersQuery = buildPropertyDetailsFilterQuery(query);
      const enrichedData = await Promise.all(
        feature.data.map(async (item: any) => {
          const property = item.data;
          const propId = property.id || property.propertyId || property.sfid;
          const details = await PropertiesService.getPropertyDetailsByID(propId, filtersQuery);
          return {
            ...item,
            data: mergePropertyDetails(property, details, propId),
          };
        }),
      );
      if (requestId !== popupRequestIdRef.current) return;
      setCurrentMarker({ ...feature, data: enrichedData });
      onHover && onHover(enrichedData[0]?.data?.id ?? null);
    } catch {
      if (requestId !== popupRequestIdRef.current) return;
      setCurrentMarker(feature);
      setCurrentNearbyProperty(null);
      onHover && onHover(feature.data[0]?.data?.id ?? null);
    } finally {
      if (requestId === popupRequestIdRef.current) {
        setNearbyLoading(null);
      }
    }
  }, [onHover, query]);

  const handleMarkerClick = (feature: Feature) => {
    if (!mapRef.current) return;

    const coords =
      resolvePinCoordinates(feature.data[0]?.data) ??
      resolvePinCoordinates({ geometry: feature.data[0]?.geometry });
    if (coords) {
      setActivePopupCoords(coords);
    }

    onHover && onHover(feature.data[0].data.id);
    setCurrentMarker(feature);
    setCurrentNearbyProperty(null);
  };

  const handleMapPinClick = useCallback((feature: Feature) => {
    if (markerVariant === "teal-pin") {
      if ((feature.count ?? 1) > 1) {
        void openGroupedPropertyDetailsPopup(feature);
        return;
      }
      void openPropertyDetailsPopup(feature.data[0].data);
      return;
    }

    handleMarkerClick(feature);
  }, [markerVariant, openGroupedPropertyDetailsPopup, openPropertyDetailsPopup]);

  const handleNearbyPropertyClick = useCallback(async (property: any) => {
    await openPropertyDetailsPopup(property);
  }, [openPropertyDetailsPopup]);

  useEffect(() => {
    if (loading || !mapLoaded) return;
    updateUnclusteredPoints();
    if (!hasInitialCalibrationRef.current) {
      if (fitMapToQueryViewport(0)) {
        return;
      }
      if (!fitMapToLocationPolygon(true)) {
        lastFittedPolygonBoundsKeyRef.current = "";
        fitMapToMarkers(true);
      } else if (resolvedPolygonBoundsKey) {
        lastFittedPolygonBoundsKeyRef.current = resolvedPolygonBoundsKey;
      }
      return;
    }

    // Same-street address searches keep the URL but load a new cookie-backed
    // polygon. Refit when bounds change outside viewport-driven map searches.
    // When polygon disappears, fall back to fitting markers once.
    if (!isViewportDrivenQuery) {
      if (
        resolvedPolygonBoundsKey &&
        resolvedPolygonBoundsKey !== lastFittedPolygonBoundsKeyRef.current
      ) {
        lastFittedPolygonBoundsKeyRef.current = resolvedPolygonBoundsKey;
        fitMapToLocationPolygon(true);
      } else if (
        !resolvedPolygonBoundsKey &&
        lastFittedPolygonBoundsKeyRef.current
      ) {
        lastFittedPolygonBoundsKeyRef.current = "";
        fitMapToMarkers(true);
      }
    }
    // Intentionally *not* depending on fitMapToMarkers/updateUnclusteredPoints to avoid
    // parent prop identity changes (e.g. parsedQuery object) causing render loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, mapLoaded, data.length, nearbyListings.length, nearByProperties.length, pinCount, clusterData.features.length, resolvedPolygonBounds, resolvedPolygonBoundsKey, isViewportDrivenQuery]);

  useEffect(() => {
    if (!mapLoaded) return;
    updateUnclusteredPoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded, data.length, nearbyListings.length, nearByProperties.length, pinCount, clusterData.features.length]);

  // Close popup when filters/sort/workspace filters change.
  useEffect(() => {
    if (!mapLoaded) return;
    if (prevFilterQueryKeyRef.current !== null && prevFilterQueryKeyRef.current !== filterQueryKey) {
      clearOpenMarkerPopup();
    }
    prevFilterQueryKeyRef.current = filterQueryKey;
  }, [filterQueryKey, mapLoaded, clearOpenMarkerPopup]);

  // Close popup while a new search is loading.
  useEffect(() => {
    if (!mapLoaded || !loading) return;
    if (currentMarker || currentNearbyProperty) {
      clearOpenMarkerPopup();
    }
  }, [loading, mapLoaded, currentMarker, currentNearbyProperty, clearOpenMarkerPopup]);

  // Close popup if the open property is no longer in map pin data.
  useEffect(() => {
    if (!mapLoaded || loading) return;

    const openProperty = currentNearbyProperty ?? currentMarker?.data?.[0]?.data;
    if (!openProperty) return;

    const openIds = [openProperty.id, openProperty.propertyId, openProperty.sfid]
      .filter((value) => value != null && value !== "")
      .map(String);
    if (!openIds.length) return;

    const pinIds = collectMapPinIds(filteredPins);
    const stillOnMap = openIds.some((id) => pinIds.has(id));
    if (!stillOnMap) {
      clearOpenMarkerPopup();
    }
  }, [
    mapLoaded,
    loading,
    mapPinIdsKey,
    mapVisiblePinIdsKey,
    currentNearbyProperty,
    currentMarker,
    data,
    nearbyListings,
    filteredPins,
    clearOpenMarkerPopup,
  ]);

  // Close any open popup when viewport search params change (after map pan/search).
  useEffect(() => {
    if (!mapLoaded) return;
    clearOpenMarkerPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapLoaded,
    parsedQuery?.minLatitude,
    parsedQuery?.maxLatitude,
    parsedQuery?.minLongitude,
    parsedQuery?.maxLongitude,
  ]);

  // Watch for location changes in parsedQuery or data to update map position when navigating
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    const currentLat = parsedQuery?.latitude ? parseFloat(parsedQuery.latitude) : null;
    const currentLng = parsedQuery?.longitude ? parseFloat(parsedQuery.longitude) : null;

    const isViewportDrivenQuery = Boolean(
      parsedQuery?.minLatitude &&
      parsedQuery?.maxLatitude &&
      parsedQuery?.minLongitude &&
      parsedQuery?.maxLongitude,
    );

    // If the user has started interacting with the map (drag/zoom), don't "snap back" the camera
    // in response to incoming data updates. Only allow camera changes when explicitly recalibrating.
    //
    // Exception: when the URL changes due to a new search location (lat/lng change WITHOUT bounds),
    // we should re-center/re-fit even if the user previously dragged the map.
    if (hasUserInteractedRef.current && !recalibrate && isViewportDrivenQuery) {
      return;
    }

    // Determine the target location - prefer parsedQuery, fallback to first marker
    let targetLat: number | null = null;
    let targetLng: number | null = null;

    if (currentLat && currentLng) {
      targetLat = currentLat;
      targetLng = currentLng;
    } else if (data.length > 0) {
      targetLat = data[0].geolocationLat;
      targetLng = data[0].geolocationLong;
    } else if (nearbyListings.length > 0) {
      targetLat = nearbyListings[0].geolocationLat;
      targetLng = nearbyListings[0].geolocationLong;
    }

    if (!targetLat || !targetLng) return;

    const currentPlaceId = parsedQuery?.placeId ? String(parsedQuery.placeId) : "";
    const currentText = parsedQuery?.text ? String(parsedQuery.text) : "";
    const currentLatKey = String(parsedQuery?.latitude ?? "");
    const currentLngKey = String(parsedQuery?.longitude ?? "");
    const currentCity = String(parsedQuery?.city ?? "");
    const currentState = String(parsedQuery?.state ?? "");
    const currentCountry = String(parsedQuery?.country ?? "");
    const lastSearchContext = lastSearchContextRef.current;
    const hasSearchContextChanged =
      !lastSearchContext ||
      lastSearchContext.placeId !== currentPlaceId ||
      lastSearchContext.text !== currentText ||
      lastSearchContext.lat !== currentLatKey ||
      lastSearchContext.lng !== currentLngKey ||
      lastSearchContext.city !== currentCity ||
      lastSearchContext.state !== currentState ||
      lastSearchContext.country !== currentCountry;

    // For map move/zoom searches, keep the user-controlled camera after initial calibration.
    if (isViewportDrivenQuery && hasInitialCalibrationRef.current && !recalibrate && !pendingRecalibrateRef.current) {
      lastDataRef.current = [...data];
      return;
    }

    // Recenter whenever the user changes location (search bar, breadcrumbs, etc.).
    if (hasSearchContextChanged || recalibrate || pendingRecalibrateRef.current) {
      lastSearchContextRef.current = {
        placeId: currentPlaceId,
        text: currentText,
        lat: currentLatKey,
        lng: currentLngKey,
        city: currentCity,
        state: currentState,
        country: currentCountry,
      };
      lastDataRef.current = [...data];
      hasInitialCalibrationRef.current = true;

      // Mark as programmatic move to prevent API call
      isProgrammaticMoveRef.current = true;

      const map = mapRef.current.getMap();
      const fittedToPolygon = fitMapToLocationPolygon(true);
      if (fittedToPolygon) {
        setTimeout(() => {
          isProgrammaticMoveRef.current = false;
        }, 1100);
        return;
      }

      lastFittedPolygonBoundsKeyRef.current = "";

      // If we have markers, fit to them; otherwise fly to the location
      if (data.length > 0 || nearbyListings.length > 0) {
        // Calculate bounds from all markers
        const coordinates: [number, number][] = data.length
          ? data.map((p) => [p.geolocationLong, p.geolocationLat] as [number, number])
          : nearbyListings.map((p) => [p.geolocationLong, p.geolocationLat] as [number, number]);

        if (coordinates.length === 1) {
          // Single marker - center it
          map.flyTo({
            center: coordinates[0],
            zoom: 10,
            duration: 1000,
          });
        } else {
          // Multiple markers - fit bounds
          const bounds = coordinates.reduce(
            (acc, coord) => {
              return [
                [Math.min(acc[0][0], coord[0]), Math.min(acc[0][1], coord[1])],
                [Math.max(acc[1][0], coord[0]), Math.max(acc[1][1], coord[1])],
              ];
            },
            [
              [Infinity, Infinity],
              [-Infinity, -Infinity],
            ],
          );
          map.fitBounds(bounds as any, {
            padding: 100,
            duration: 1000,
          });
        }
      } else {
        map.flyTo({
          center: [targetLng, targetLat],
          zoom: 10,
          duration: 1000,
        });
      }

      // Reset flag after animation completes
      setTimeout(() => {
        isProgrammaticMoveRef.current = false;
      }, 1100);
    }
  }, [
    parsedQuery?.latitude,
    parsedQuery?.longitude,
    parsedQuery?.placeId,
    parsedQuery?.text,
    parsedQuery?.city,
    parsedQuery?.state,
    parsedQuery?.country,
    data,
    nearbyListings,
    mapLoaded,
    recalibrate,
    showSearch,
    locationPolygon,
    resolvedPolygonBounds,
    fitMapToLocationPolygon,
  ]);



  const showSearchAsIMoveCheckbox = showSearchAsIMoveOption && !showSearch;

  const showSearchHereButton = showSearch && !hideSearchButton && !loading;

  let bottomButtonText = "";
  if (!isAreaPage && !isPropertyCard && showNext && data.length) {
    bottomButtonText = `Load Next (Showing ${(currentPage - 1) * pageSize + 1} - ${(currentPage - 1) * pageSize + data.length} out of ${totalListings})`;
  } else if (!isAreaPage && !isPropertyCard && !data.length) {
    bottomButtonText = "No Results Found";
  }


  return (
    <>
      <style>{`
        @keyframes pulse-shadow {
          0%, 100% {
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.05), 0 0 20px rgba(232, 103, 73, 0.3);
          }
          50% {
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.05), 0 0 40px rgba(232, 103, 73, 0.8);
          }
        }
        /* Keep property cards above dense nearby-dot layers */
        .mapboxgl-popup {
          z-index: 5000 !important;
        }
        .oh-map-mobile-ctrls .mapboxgl-ctrl-bottom-right,
        .oh-map-mobile-ctrls .mapboxgl-ctrl-bottom-left {
          bottom: 8%;
        }
      `}</style>
      <div className='h-full w-full relative z-0 overflow-hidden rounded-md'>
        {width !== 100 && width !== 0 && showPanelControl && displayMode !== "list" && (
          <Button
            variant='outline'
            size={"icon"}
            className='absolute top-4 z-50 left-3'
            onClick={() => {
              if (setWidth) {
                if (width < 45) {
                  setWidth(45);
                } else if (width === 100) {
                  setWidth(45);
                } else if (displayMode === "list") {
                  setWidth(0);
                } else {
                  setWidth(30);
                }
              }
            }}
          >
            {width >= 45 ? (
              <PanelLeftOpen aria-label='shrink map' />
            ) : (
              <PanelRightOpen aria-label='expand map' />
            )}
          </Button>
        )}

        {/* Search-as-you-move checkbox */}
        {showSearchAsIMoveCheckbox && (
          <div className={`absolute ${mobile ? "top-20" : "top-4"} left-1/2 -translate-x-1/2 bg-white rounded-md shadow-md border border-gray-200 p-2 flex items-center gap-2 z-[10000] whitespace-nowrap`}>
            <Checkbox
              id="search-as-i-move"
              checked={searchAsIMove}
              className="border-oh-primary data-[state=checked]:bg-oh-primary data-[state=checked]:border-oh-primary data-[state=checked]:text-white [&_svg]:stroke-[3] shrink-0"
              onCheckedChange={(checked) => {
                const next = checked === true;
                setSearchAsIMove(next);
                if (next) {
                  setShowSearch(false);
                  setShouldPulseSearchHere(false);
                }
              }}
            />
            <label
              htmlFor="search-as-i-move"
              className="text-xs md:text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              Search as I move the map
            </label>
          </div>
        )}

        {showSearchHereButton && (
          <div
            className={
              "absolute top-4 left-1/2 -translate-x-1/2 bg-oh-secondary text-white rounded-full shadow-md px-4 py-2 flex items-center gap-2 z-[10000] " +
              (shouldPulseSearchHere ? "animate-[pulse-shadow_2s_ease-in-out_infinite]" : "")
            }
          >
            <button
              type="button"
              onClick={() => {
                handleGeoSearch();
              }}
              className="text-sm font-semibold cursor-pointer text-white flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              <span>Redo search here</span>
            </button>
          </div>
        )}

        {/* Render map only once - always mounted, never unmounted or recreated */}
        <div
          ref={mapContainerRef}
          className={`${isOpen ? "hidden" : "block h-full w-full"}${mobile || isMobile ? " oh-map-mobile-ctrls" : ""}`}
          style={{ display: isOpen ? 'none' : 'block' }}
        >
          <Map
            key="map-instance" // Stable key to prevent re-mounting
            style={{ width: "100%", height: "100%" }}
            trackResize
            antialias
            attributionControl={false}
            ref={mapRef}
            scrollZoom={isScrollZoom}
            onDragStart={(event) => {
              if (isProgrammaticMoveRef.current) return;
              if (!(event as any)?.originalEvent) return;
              clearOpenMarkerPopup();
            }}
            onDragEnd={(event) => {
              updateUnclusteredPoints();

              // Skip if this is a programmatic move (initial fit or recalibration)
              if (isProgrammaticMoveRef.current) {
                return;
              }

              // Only react to real user interactions, not map animations.
              if (!(event as any)?.originalEvent) {
                return;
              }

              // Mark that user has interacted with the map
              hasUserInteractedRef.current = true;

              if (!mapRef.current) return;
              const map = mapRef.current.getMap();
              const bounds = map.getBounds();

              const minLongitude = bounds.getWest();
              const maxLongitude = bounds.getEast();
              if (minLongitude <= -180 || maxLongitude >= 180) {
                return;
              }

              if (searchAsIMove) {
                // Auto-search - only if user has interacted
                handleGeoSearch();
              } else {
                // Manual search
                setShowSearch(true);
                setShouldPulseSearchHere(true);
              }
            }}
            onLoad={() => {
              const zoom = mapRef.current?.getMap?.()?.getZoom?.() ?? 5;

              if (data.length) {
                setMapCenter({
                  lat: data[0].geolocationLat || (parsedQuery?.latitude ? parseFloat(parsedQuery.latitude) : -33.8688197),
                  long: data[0].geolocationLong || (parsedQuery?.longitude ? parseFloat(parsedQuery.longitude) : 151.2092955),
                  zoom,
                });
              } else if (nearbyListings.length) {
                setMapCenter({
                  lat: nearbyListings[0].geolocationLat || (parsedQuery?.latitude ? parseFloat(parsedQuery.latitude) : -33.8688197),
                  long: nearbyListings[0].geolocationLong || (parsedQuery?.longitude ? parseFloat(parsedQuery.longitude) : 151.2092955),
                  zoom,
                });
              } else if (parsedQuery?.latitude && parsedQuery?.longitude) {
                setMapCenter({
                  lat: parseFloat(parsedQuery.latitude),
                  long: parseFloat(parsedQuery.longitude),
                  zoom,
                });
              } else {
                const firstNearby = (Array.isArray(nearByProperties) ? nearByProperties : []).find(
                  (p: any) =>
                    (p?.geolocationLat && p?.geolocationLong) || (p?.lat && p?.lng),
                ) as any;
                if (firstNearby) {
                  const lat = firstNearby.geolocationLat ?? firstNearby.lat;
                  const lng = firstNearby.geolocationLong ?? firstNearby.lng;
                  if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
                    setMapCenter({
                      lat: Number(lat),
                      long: Number(lng),
                      zoom,
                    });
                  }
                }
              }

              updateUnclusteredPoints();
              if (fitMapToQueryViewport(0)) {
                setMapLoaded(true);
                return;
              }
              const fittedToPolygon = fitMapToLocationPolygon(true);
              if (
                !fittedToPolygon &&
                (data.length ||
                  nearbyListings.length ||
                  (Array.isArray(nearByProperties) && nearByProperties.length))
              ) {
                lastFittedPolygonBoundsKeyRef.current = "";
                fitMapToMarkers(true);
              }

              setMapLoaded(true);
            }}
            initialViewState={{
              latitude: parsedQuery?.latitude
                ? parseFloat(String(parsedQuery.latitude))
                : queryViewportBounds
                  ? (queryViewportBounds.minLatitude + queryViewportBounds.maxLatitude) / 2
                  : 0,
              longitude: parsedQuery?.longitude
                ? parseFloat(String(parsedQuery.longitude))
                : queryViewportBounds
                  ? (queryViewportBounds.minLongitude + queryViewportBounds.maxLongitude) / 2
                  : 0,
              zoom: parsedQuery?.zoom
                ? parseFloat(String(parsedQuery.zoom))
                : queryViewportBounds
                  ? getZoomForBounds(queryViewportBounds)
                  : 5,
            }}
            maxZoom={MAP_MAX_ZOOM}
            onMoveEnd={(e) => {
              setMapCenter({
                lat: e.viewState.latitude || 0,
                long: e.viewState.longitude || 0,
                zoom: e.viewState.zoom ?? 0,
              });
            }}
            touchZoomRotate={isMobile && !isOpen ? false : true}
            dragPan={isMobile && !isOpen ? false : true}
            onZoomEnd={(event) => {
              updateUnclusteredPoints();

              // Skip if this is a programmatic move (initial fit or recalibration)
              if (isProgrammaticMoveRef.current) {
                return;
              }

              // Only react to real user interactions, not map animations.
              if (!(event as any)?.originalEvent) {
                return;
              }

              // Mark that user has interacted with the map
              hasUserInteractedRef.current = true;

              if (!mapRef.current) return;
              const map = mapRef.current.getMap();
              const bounds = map.getBounds();

              const minLongitude = bounds.getWest();
              const maxLongitude = bounds.getEast();
              if (minLongitude <= -180 || maxLongitude >= 180) {
                return;
              }

              if (searchAsIMove) {
                // Auto-search - only if user has interacted
                handleGeoSearch();
              } else {
                // Manual search
                setShowSearch(true);
                setShouldPulseSearchHere(true);
              }
            }}
            mapStyle={getMapboxStyle()}
            mapboxAccessToken={getMapboxAccessToken()}>
            {mapLoaded && locationPolygon ? (
              <Source
                id="location-polygon"
                type="geojson"
                data={locationPolygon as any}
              >
                <Layer
                  id="location-polygon-fill"
                  type="fill"
                  paint={{
                    "fill-color": MAP_POLYGON_FILL,
                    "fill-opacity": 0.09,
                  }}
                />
                <Layer
                  id="location-polygon-outline"
                  type="line"
                  paint={{
                    "line-color": MAP_POLYGON_OUTLINE,
                    "line-width": 2,
                    "line-opacity": 0.75,
                    "line-dasharray": [3, 2],

                  }}
                />
              </Source>
            ) : null}

            {mapLoaded && filteredNearByProperties.map((property: any, idx) => {
              const lat = property?.geolocationLat ?? property?.lat;
              const lng = property?.geolocationLong ?? property?.lng;
              if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return null;

              const key = property?.propertyId ?? property?.id ?? property?.sfid ?? idx;
              return (
                <NearbyPropertyPin
                  key={String(key)}
                  lat={Number(lat)}
                  long={Number(lng)}
                  onClick={() => {
                    void handleNearbyPropertyClick(property);
                  }}
                />
              );
            })}
            {mapLoaded && visibleClusters.map((clusterFeature) => {
              const [lng, lat] = clusterFeature.geometry.coordinates;
              const count = clusterFeature.properties?.point_count ?? 0;
              const clusterId = clusterFeature.properties?.cluster_id ?? `${lng},${lat}`;
              const isClusterHovered =
                typeof clusterId === "number" && hoveredClusterIds.has(clusterId);

              return (
                <ClusterBubble
                  key={`cluster-${clusterId}`}
                  lat={lat}
                  long={lng}
                  count={count}
                  priceTier={resolveClusterPriceTier(clusterFeature)}
                  isHovered={isClusterHovered}
                  onClick={() => handleClusterBubbleClick(clusterFeature)}
                />
              );
            })}
            {mapLoaded && unclusteredPoints.map((feature) => {

              const pinRecords = feature.data?.map((item: Feature) => resolveFeaturePinRecord(item)) ?? [];
              const primaryPin = pinRecords[0];

              const pinCoords = resolvePinCoordinates(primaryPin) ?? {
                lng: feature.geometry?.coordinates?.[0],
                lat: feature.geometry?.coordinates?.[1],
              };
              const priceTier = resolveHighestPriceTier(
                pinRecords.map((record: any) => resolvePriceTier(record)),
              );
              const markerKey = primaryPin?.id ?? primaryPin?.propertyId ?? `${pinCoords.lng},${pinCoords.lat}`;

              return (
                <CustomMarker
                  key={String(markerKey)}
                  lat={Number(pinCoords.lat)}
                  long={Number(pinCoords.lng)}
                  text={isWorkspaceMap ? primaryPin?.name : convertCurrency(
                    showPerDesk
                      ? primaryPin?.avgMonthlyCostPerWorkstation || primaryPin?.costPerWorkstation
                      : primaryPin?.priceMonthly,
                    primaryPin?.currencyIsoCode ||
                    primaryPin?.currency_iso_code ||
                    primaryPin?.currency ||
                    tierCurrencyIsoCode ||
                    regionCurrency,
                    currency,
                  )}
                  setCurrentMarker={() => {
                    handleMapPinClick(feature);
                  }}
                  count={feature.count}
                  variant={markerVariant}
                  priceTier={priceTier}
                  isHovered={
                    feature.data?.length
                      ? feature.data.some((f: Feature) => matchesMapPinHover(f.data, activeHoverIds))
                      : false
                  }
                />
              );
            })}

            {mapLoaded && currentMarker && activePopupCoords && (() => {
              const popupPosition = getPopupPosition(activePopupCoords.lng, activePopupCoords.lat);

              return (
                <Popup
                  longitude={activePopupCoords.lng}
                  latitude={activePopupCoords.lat}
                  anchor={popupPosition.anchor}
                  onClose={() => {
                    onHover && onHover(null);
                    setCurrentMarker(null);
                  }}
                  closeButton={false}
                  maxWidth='auto'
                  offset={popupPosition.offset}
                  className='rounded-lg overflow-hidden border-none bg-transparent p-0 z-[5000]'
                >
                  <MapCard
                    currentMarkers={currentMarker.data}
                    onClose={() => {
                      onHover && onHover(null);
                      setCurrentMarker(null);
                    }}
                    mobile={mobile}
                    isPropertyCard={isPropertyCard}
                    featuredPropertyIds={featuredPropertyIds}
                  />
                </Popup>
              );
            })()}
            {mapLoaded && !nearbyLoading && currentNearbyProperty && activePopupCoords && (() => {
              const popupPosition = getPopupPosition(activePopupCoords.lng, activePopupCoords.lat);

              // Convert nearby property to Feature-like structure for MapCard
              const featureData = [{
                data: currentNearbyProperty,
                geometry: {
                  type: "Point",
                  coordinates: [activePopupCoords.lng, activePopupCoords.lat],
                },
              }];

              return (
                <Popup
                  longitude={activePopupCoords.lng}
                  latitude={activePopupCoords.lat}
                  anchor={popupPosition.anchor}
                  onClose={() => {
                    onHover && onHover(null);
                    setCurrentNearbyProperty(null);
                  }}
                  closeButton={false}
                  maxWidth='auto'
                  offset={popupPosition.offset}
                  className='rounded-lg overflow-hidden border-none bg-transparent p-0 z-[5000]'
                >
                  <MapCard
                    currentMarkers={featureData}
                    onClose={() => {
                      onHover && onHover(null);
                      setCurrentNearbyProperty(null);
                    }}
                    mobile={mobile}
                    isPropertyCard={isPropertyCard}
                    featuredPropertyIds={featuredPropertyIds}
                  />
                </Popup>
              );
            })()}
            {mapLoaded && nearbyLoading && (
              <Popup
                longitude={nearbyLoading.lng}
                latitude={nearbyLoading.lat}
                anchor="top"
                closeButton={false}
                maxWidth='auto'
                offset={12}
                className='rounded-lg overflow-hidden border-none bg-transparent p-0 z-[5000]'
              >
                <div className="bg-white px-3 py-2 rounded shadow text-xs flex items-center gap-2">
                  <span className="inline-block w-3 h-3 rounded-full border-2 border-gray-300 border-t-gray-700 animate-spin" />
                  Loading…
                </div>
              </Popup>
            )}

            {!hideSearchButton && !loading && bottomButtonText ? (
              <div className="pointer-events-none">
                <Button
                  variant='default'
                  className="absolute z-[10000] bottom-8 left-[50%] translate-x-[-50%] flex items-center justify-center pointer-events-auto"
                  onClick={() => {
                    handlePageChange?.(currentPage + 1);
                  }}
                >
                  {bottomButtonText}
                </Button>
              </div>
            ) : null}
            <NavigationControl position='bottom-right' />
          </Map>
        </div>

        {showPinTierLegend && mapLoaded ? (
          <MapPinTierLegend
            tiers={mapPriceTiersPresent}
            selectedTiers={effectiveSelectedTiers}
            tierStartingPrices={tierStartingPrices}
            currency={currency}
            compact={mobile || isMobile}
            onToggleTier={(tier) => {
              const base = effectiveSelectedTiers;
              const set = new Set(base);
              if (set.has(tier)) set.delete(tier);
              else set.add(tier);
              const next = MAP_PIN_TIER_ORDER.filter((item) => set.has(item));
              mapPinTierSelectionCache = {
                locationKey: locationContextKey,
                tiers: next,
              };
              setVisibleTiers(next);
              applyTierPriceFilter(next, { fromUserToggle: true });
              clearOpenMarkerPopup();
            }}
          />
        ) : null}
      </div>
    </>
  );
}
